import React, { useRef, useState, useEffect } from "react"
import { Title, Text } from "@ui/Typography"
import styled from "@emotion/styled"
import { CenterBlock } from "@ui/Block"
import Box from "@ui/Box"
import { ColorProps, color, SpaceProps, space } from "styled-system"

const Dialog = styled.div`
  position: absolute;
  z-index: 1;
  box-shadow: 1px 0 3px rgba(0, 0, 0, 0.16), 3px 0 9px rgba(0, 0, 0, 0.08);
  background-color: #fff;
  display: block;
  visibility: hidden;
  margin: 0.5rem;
`

const DialogScroll = styled.div`
  overflow: auto;
  max-height: 60vh;
`

type Dialog = {
  title: string
  text: string
}

const MapContainer = styled.div`
  overflow: hidden;
  width: 100%;
`

const dialogs: Record<string, Dialog> = {
  myanmar: {
    title: "Myanmar",
    text:
      "Myanmar has multiple ethno-nationalist insurgencies, many that have lasted for six decades. By some estimates, conflict affects close to 60 percent of the state’s territories. Several ethnic armed groups have been in peace talks with the government since 2013, leading some to sign a Nationwide Ceasefire Agreement. However, serious clashes continue to occur in areas dominated by the remaining ethnic groups. Military operations in Rakhine, in retaliation for attacks mounted in 2016 by the small Arakan Rohingya Salvation Army, resulted in 750,000 Rohingya fleeing to Bangladesh in a matter of months; 600,000 Rohingya remain in Myanmar. The scale of hate speech against the Rohingya and other ethnic minorities illustrates how citizenship and identity are at the heart of this response and may continue to divide society in the country if nothing is done.",
  },
  thailand: {
    title: "Thailand",
    text:
      "After years of political instability, coups and violent street protests, national elections in 2019 led to a democratically elected parliament. Nonetheless, underlying sources of deep fragility remain, revolving around political and identity-based marginalization, a lack of voice, stark regional inequalities, issues relating to access to justice, natural resource management issues, which, if left unaddressed, may lead to violence. Against this broader background, violence in the three Southern Border Provinces continues to take its toll, although the nature of this violence remains widely misunderstood. While efforts continue towards meaningful engagement between various parties, after 15 years, more than 7,000 lives have been lost. This has caused fractures in formerly peaceful social relations particularly amongst youth. These various factors risk driving hate speech, radicalization and forms of violent extremism against people of different identities, religions, socio-economic statuses, and political beliefs.",
  },
  philippines: {
    title: "Philippines",
    text:
      "For the past four decades, there have been two significant insurgencies in Mindanao, the second largest island comprising the Philippines. The first, a communist rebellion led by the New People’s Army and the Communist Party of the Philippines who, despite occasional breakdowns of ceasefire, have been in talks with the government since 2001. Second, the Moro separatist struggle in the southern Bangsamoro region. The struggle was led by the Moro National Liberation Front until entering a peace agreement with the state in 1996. After which the insurgency was continued by the Moro Islamic Liberation Front (MILF) who subsequently signed a comprehensive peace agreement in 2014, leading to the inauguration of the Bangsamoro Autonomous Region of Muslim Mindanao in 2019. <br/><br/> Several groups, including the Maute Group, Abu Sayyaf Group and the Bangsamoro Islamic Freedom Fighters, remain outside of this peace process. The 2017 IS-inspired Marawi Siege and the Jolo bombings on 27 January 2019 highlight the risk of external drivers in local conflict. How recovery and reconciliation are handled will likely determine whether support for the Islamic State grows or if it can be limited. The success of the Bangsamoro Autonomous Region of Muslim Mindanao (BARMM) will also be critical in reducing the appeal of extremism in the Philippines.",
  },
  malaysia: {
    title: "Malaysia",
    text:
      "Malaysia does not suffer any long-term civil conflict and has not experienced significant domestic terrorist events in recent decades. However, home-grown militants have a history of becoming involved in and becoming key players in both regional and international conflicts. Malaysian fighters who travelled to Afghanistan in the 1980s returned to Malaysia and became involved in terrorist attacks and regional insurgencies in the Southern Philippines, as well as Ambon and Poso in Indonesia. Among the well-known figures are Azahari Hussin, Nordin Mat Top, Mahmud Ahmad, Zulfikli Hir, and Amin Baco. Since 2011, a significant number of Malaysians have travelled or been apprehended by Malaysian authorities en route to Syria and Iraq to join IS and other militant groups. The fall of IS in 2018 has raised concern in Malaysia, as in Indonesia, about returning foreign fighters and their families. Malaysia has adopted a policy of repatriating foreign fighters and their families and working to disengage them from violence.",
  },
  indonesia: {
    title: "Indonesia",
    text:
      "Indonesia has a diverse conflict landscape that spans Aceh, Ambon, Northern Maluku and Poso. It has also been home to several violent extremist groups, from Darul Islam to Jemaah Islamiyah (JI). Members of JI were convicted in 2008 for their role in the 2002 Bali Bombings. The group also has historical links with Al-Qaeda and networks in both Indonesia and Malaysia. A number of its leaders fought or trained in Afghanistan. Sustained efforts by the Indonesia government has significantly contained JI and have prevented (or inhibited) IS-linked groups from exploiting old networks of violent extremist groups. At this time, extremist violence is at its lowest ebb in Indonesia. The country ranks 35th on the 2019 Global Terrorism Index. Only two people per million Muslims in Indonesia left home to fight with IS in the Middle East.",
  },
}

const MapSvg = styled.svg`
  min-width: 34rem;

  [data-id] {
    fill: rgba(255, 255, 255, 0);
  }

  [aria-selected="true"],
  .mouseover {
    fill: #ff3e4c;
    cursor: pointer;
  }
`

const Emphasis = styled.em<ColorProps & SpaceProps & { color?: string }>`
  font-style: italic;
  display: block;
  ${color};
  ${space};
`

const CloseDialog = styled.button`
  position: absolute;
  right: 1rem;
  top: 1rem;
  cursor: pointer;
  display: block;
  padding: 0.5rem;

  svg {
    display: block;
  }

  appearance: none;
  background: transparent;
  border: 0;

  opacity: 0.7;
  transition: opacity 200ms cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover,
  &:focus {
    outline: 0;
    opacity: 1;
  }
`

type DialogState = {
  id: string
  dialog: Dialog
  position: DOMRect
  offset: number
}

const Map = () => {
  const blockRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLDivElement>(null)

  const [currentDialog, setDialog] = useState<DialogState | null>(null)

  const onMouseOver = (event: React.MouseEvent<SVGPathElement, MouseEvent>) => {
    if (!event.currentTarget.classList.contains("mouseover")) {
      event.currentTarget.classList.add("mouseover")
    }
  }
  const onMouseOut = (event: React.MouseEvent<SVGPathElement, MouseEvent>) => {
    event.currentTarget.classList.remove("mouseover")
  }

  const onClick = (event: React.MouseEvent<SVGPathElement, MouseEvent>) => {
    if (!ref.current || !blockRef.current) {
      return null
    }

    const mapId = event.currentTarget.getAttribute("data-id") || ""

    const dialog = dialogs[mapId]

    setDialog(
      dialog
        ? {
            id: mapId,
            dialog,
            position: event.currentTarget.getBoundingClientRect(),
            offset: parseInt(
              event.currentTarget.getAttribute("data-offset-top") || "0"
            ),
          }
        : null
    )
  }

  useEffect(() => {
    if (!ref.current || !blockRef.current) {
      return
    }

    if (!currentDialog) {
      ref.current.style.visibility = "hidden"
      ref.current.style.pointerEvents = "none"
      ref.current.style.left = ``
      ref.current.style.right = ``

      return
    }

    ref.current.style.visibility = "visible"
    ref.current.style.pointerEvents = "all"

    const dialogPos = ref.current.getBoundingClientRect()

    const mapPos = currentDialog.position
    const blockPos = blockRef.current.getBoundingClientRect()

    ref.current.style.top = `${mapPos.top -
      blockPos.top +
      currentDialog.offset}px`

    let leftPos = mapPos.right + 32

    if (leftPos + dialogPos.width < blockPos.width) {
      ref.current.style.left = `${mapPos.right + 32}px`
    } else {
      ref.current.style.right = `0px`
    }
  }, [currentDialog])

  const closeDialog = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (!ref.current || !blockRef.current) {
      return
    }

    setDialog(null)
  }

  return (
    <CenterBlock
      ref={blockRef}
      bg="wine"
      p="0"
      py="4xl"
      pl="xl"
      pr={0}
      zIndex={2}
    >
      <Box maxWidth="60rem" width="100%">
        <Dialog ref={ref}>
          <CloseDialog aria-label="Close" onClick={closeDialog}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24"
              viewBox="0 0 24 24"
              width="24"
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              <path d="M0 0h24v24H0z" fill="none" />
            </svg>
          </CloseDialog>
          <Title
            color="coral"
            fontFamily="effect"
            fontSize="lg"
            m="0"
            p="xl"
            pb="md"
          >
            {currentDialog?.dialog?.title}
          </Title>
          <DialogScroll>
            <Text
              m="0"
              fontSize="md"
              width="100%"
              maxWidth="50ch"
              p="xl"
              pt="0"
              dangerouslySetInnerHTML={{
                __html: currentDialog?.dialog?.text || "",
              }}
            />
          </DialogScroll>
        </Dialog>

        <Text
          color="coral"
          mt="0"
          mb="xs"
          fontSize={["2xl", "4xl"]}
          variant="effect"
        >
          South-East Asia – a region in flux
        </Text>
        <Text
          fontSize="md"
          maxWidth="100ch"
          color="#fff"
          mt="0"
          mb="xl"
          pr="xl"
        >
          There are complex push and pull factors that lead people to violence.
          Conflicts as well as localized grievances can create environments that
          are conducive to violent extremism. Understanding and investigating
          these underlying causes can help create more resilient and peaceful
          communities.
          <br />
          <Emphasis mt="xs" color="#fff">
            Click on the map to read more about issues that affect violent
            extremism in each country.
          </Emphasis>
        </Text>
        <MapContainer>
          <MapSvg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            viewBox="0 0 1144.363 630.891"
          >
            <g id="map" transform="translate(-453.869 -2448.279)">
              <path
                onMouseEnter={onMouseOver}
                onClick={onClick}
                onMouseLeave={onMouseOut}
                fill="transparent"
                id="thailand"
                data-id="thailand"
                aria-selected={currentDialog?.id === "thailand"}
                d="M194.605,358.125c.091-.3.16-.6.227-.891.042-.182.091-.362.123-.544.062-.332.1-.67.126-1.009.015-.148.039-.293.049-.443a11.492,11.492,0,0,0-.02-1.509,21.9,21.9,0,0,0-2.309-7.393c-.084-.18-.145-.337-.234-.519h0a23.12,23.12,0,0,1-2.592-9.877,22.353,22.353,0,0,1,2.41-10.018c.724-1.531,1.273-3.016,1.768-4.358,1.258-3.407,2.208-5.983,4.739-6.655.059-.017.128-.022.19-.034a5.273,5.273,0,0,1,.687-.108,10.261,10.261,0,0,1,3.732.5,31.874,31.874,0,0,0,7.972,1.659c.273.007.547.01.822,0,.091,0,.187-.01.281-.012a23.363,23.363,0,0,0,2.408-.231c1.315-.192,2.834-.492,4.744-.894a44.519,44.519,0,0,1,9.786-1.292,15.425,15.425,0,0,1,1.952.175c.207.032.4.1.606.138.362.074.734.135,1.083.241l.01-.03c.118-.4.229-.822.337-1.248l.039-.16c.1-.411.194-.825.283-1.236l.049-.231q.122-.591.229-1.167l.052-.273c.066-.367.128-.724.182-1.064l.047-.273c.052-.322.1-.633.14-.918l.039-.266c.034-.251.069-.485.1-.692.015-.1.025-.195.037-.288s.027-.2.037-.286c.032-.266.054-.451.059-.5a31.814,31.814,0,0,0-1.753-11.1,40.426,40.426,0,0,0-5.667-11.022c-2.489-3.255-2.489-9.311-2.489-16.1v0c0-3.444-1.677-4.1-3.742-4.9A11.479,11.479,0,0,1,216,259.979c-.049-.057-.1-.1-.145-.163-.084-.1-.187-.194-.278-.293-1.428-1.512-3.883-2.4-6.546-2.858-.9-.155-1.807-.251-2.711-.32a28.379,28.379,0,0,0-8.435.468c-.63.185-1.4.455-2.28.766-3.425,1.2-8.565,3.009-14.309,3.048a34.076,34.076,0,0,0-3.233,2.263c-2.162,1.635-4.419,3.336-6.448,3.646a4.044,4.044,0,0,1-.94.042c-3.277-.278-5.33-5.022-3.974-20.961,1.162-13.669.426-14.868-3.021-20.476l-.606-.992c-.175-.052-.342-.113-.512-.167-.251-.081-.5-.165-.746-.258a10.249,10.249,0,0,1-2.755-1.534h0a8.238,8.238,0,0,1-2.777-4.23l0-.007-.084-.3-.025,0c-.143-.017-.323-.034-.517-.049l-.158-.012c-.217-.015-.455-.027-.714-.032h-.113c-.246,0-.507,0-.778.015-.057,0-.111,0-.17,0-.3.017-.606.047-.921.089-.069.007-.14.02-.209.03a9.985,9.985,0,0,0-1,.194l-.069.02a8.359,8.359,0,0,0-.95.308c-.064.022-.126.044-.19.071a6.894,6.894,0,0,0-.933.47c-.062.034-.121.076-.182.113a6.234,6.234,0,0,0-.884.677l0,0c-.177.167-.35.313-.524.473a23.51,23.51,0,0,1-4.52,3.274c-.059.032-.113.069-.175.1-.244.133-.515.259-.773.386a45.566,45.566,0,0,1-6.714,2.595,32.1,32.1,0,0,0-7.186,2.991,39.024,39.024,0,0,0-6.408,5.133c-4.67,4.35-4.658,7.2-4.638,11.231v0l0,.849h0c0,1.5,1,2.9,2.154,4.395.084.106.158.209.241.318l.268.352c2.063,2.681,4.235,5.771,2.9,10.791-1.357,5.091-.015,6.258,1.588,7.647a10.553,10.553,0,0,1,2.435,2.76c.047.084.1.153.148.239.074.138.18.251.263.382a12.515,12.515,0,0,0,3.555,3.166c2.548,1.748,5.291,3.629,6.16,7.908a16.5,16.5,0,0,1-.6,9.385,30.107,30.107,0,0,1-3.993,7.044c-.544.758-1.125,1.534-1.758,2.349a12.186,12.186,0,0,0-1.945,3.282,6.343,6.343,0,0,0-.2.674c-.534,2.378.928,3.777,2.563,5.34a25.642,25.642,0,0,1,1.9,1.955A22.533,22.533,0,0,1,137,321.217c.133.357.286.7.4,1.071.155.49.278,1,.4,1.517a20.677,20.677,0,0,1,.51,3.634,20.93,20.93,0,0,1-.064,3.434c-.049.441-.079.867-.091,1.28a22.866,22.866,0,0,0,1.342,7.679,31.967,31.967,0,0,1,1.632,8.223c.229,4.766-1.125,7.462-2.521,10.242a26.4,26.4,0,0,0-1.878,4.454c-.047.153-.1.293-.14.451-.116.433-.231.849-.345,1.246s-.227.78-.337,1.147c-.222.736-.438,1.418-.645,2.075a34.027,34.027,0,0,0-1.893,8.725,11.4,11.4,0,0,1-.726,3.287,10.786,10.786,0,0,1-1.938,3.289l-.007.007a11.636,11.636,0,0,1-1.059,1.061c-.123.111-.254.207-.382.313-.254.209-.51.416-.773.6-.155.111-.315.209-.473.313-.246.163-.49.318-.739.461-.17.1-.337.189-.507.278-.118.064-.239.138-.357.2s-.625.3-1.024.487a13.023,13.023,0,0,1-.616,2.533c-2.341,6.573-2.25,8.376-1.979,14.769s1.979,10.628,6.214,9.368,6.529,6.079,11.483,11.032c4.5,4.5,4.838,7.369,9.392,13.127.029-.022.064-.044.1-.067a2.835,2.835,0,0,1,.6-.374l.03-.017c.027-.012.066-.022.094-.034.17-.076.352-.148.549-.214l.187-.059c.244-.076.5-.145.773-.2l.1-.017c.249-.052.507-.093.771-.128l.236-.029c.3-.032.611-.059.923-.069h0c1.918-.059,4,.332,5.224,1.514l.007.005.039.039,0,0a28.145,28.145,0,0,1,2.149,2.536,21.511,21.511,0,0,0,4.9,4.946,7.676,7.676,0,0,0,3.777,1.3c.108.007.219.03.327.035a11.543,11.543,0,0,0,4.493-.812c.162-.062.345-.123.492-.185l0,0a3.531,3.531,0,0,0,.652-.379c.057-.037.108-.076.162-.118a6.108,6.108,0,0,0,.581-.5l.025-.025c.2-.2.387-.411.569-.63.042-.049.084-.1.126-.153.162-.2.32-.411.463-.615l.02-.027c.158-.224.3-.441.426-.643l.066-.108c.069-.111.126-.209.185-.308.17-.3.345-.628.463-.859a22.83,22.83,0,0,1-1.972-1.677c-4.141-3.964-6.485-7.026-9.456-8.016s-8.464-1.531-11.527-6.214-2.341-9.457-3.784-15.941-3.873-2.432-5.177-6.261.586-5.177-1.487-6.935-4.412,1.352-6.844-1.982-.968-6.778-1.869-12.27,1.44-6.3,3.873-10.9.99-10,5.133-15.579,3.784-9.818,3.693-16.392-.451-10.719,5.133-12.248,6.844,1.169,8.558,4.5-3.964,7.834-.542,11.168,5.313.539,11.709.45,7.295,5.584,13.058,8.464c4.656,2.329,4.466,4.333,6.308,7.772.066-.18.133-.357.194-.532C194.475,358.526,194.546,358.324,194.605,358.125Z"
                transform="translate(423.123 2359.689)"
                stroke="#ff3e4c"
                strokeWidth="1"
              />
              <g
                id="Group_729"
                data-name="Group 729"
                transform="translate(580.395 2534.588)"
                opacity="0.101"
              >
                <path
                  id="Path_272"
                  data-name="Path 272"
                  d="M139.5,222.964c-.03.079-.062.158-.094.241-.138.332-.293.687-.492,1.086a11.232,11.232,0,0,1-2.211,2.728c-1.859,1.866-3.969,3.988-2.693,8.277a6.831,6.831,0,0,0,2.3,3.513l0,0a8.794,8.794,0,0,0,2.3,1.275,14.156,14.156,0,0,0,1.462.49l.276.074.032.049.121.2.756,1.233c3.651,5.941,4.429,7.211,3.228,21.343-1.246,14.668.177,19.1,2.583,19.393.025,0,.049.012.074.015,1.714.145,4.116-1.672,6.4-3.4a32.067,32.067,0,0,1,3.565-2.467l.163-.089h.185c.7,0,1.389-.03,2.068-.079a46.21,46.21,0,0,0,11.953-2.885c.911-.32,1.7-.6,2.349-.788a26.131,26.131,0,0,1,6.517-.63,33.675,33.675,0,0,1,4.688.359c.195.03.394.049.588.084.131.022.258.052.389.076,2.994.569,5.756,1.664,7.314,3.592a10.139,10.139,0,0,0,4.68,3.036c2.575,1,4.665,1.817,4.665,6.241v0c0,6.534,0,12.364,2.194,15.23a41.97,41.97,0,0,1,5.887,11.436,33.22,33.22,0,0,1,1.824,11.63l0,.047s-.089.81-.263,2.011l-.015.089c-.022.163-.047.33-.074.5l-.032.2c-.025.145-.047.291-.071.443-.017.1-.032.192-.049.29-.022.133-.044.268-.069.406-.02.116-.039.231-.062.35s-.047.256-.071.384-.049.263-.074.4-.047.236-.071.357c-.027.143-.057.285-.086.431-.027.121-.052.244-.079.364-.03.14-.059.281-.091.421-.027.123-.057.249-.086.374s-.067.281-.1.419-.062.246-.094.369-.069.271-.106.406-.069.251-.106.377-.071.256-.108.382l-.015.049c.155.069.3.162.453.239s.278.143.416.219a12.619,12.619,0,0,1,1.514.987,4.341,4.341,0,0,0,2.949.945,12.735,12.735,0,0,0,5.005-1.839,17.17,17.17,0,0,1,3.661-1.6,14.819,14.819,0,0,1,2.787-.468,7.581,7.581,0,0,1,2.607.35c.345.091.726.192,1.221.291a5.814,5.814,0,0,0,1.14.106c.118,0,.236-.017.357-.022a17.168,17.168,0,0,0,3.011-.47l.066-.012c.3-.069.574-.128.832-.18l.106-.022v0c.091-.411.2-.874.32-1.366l.057-.234c.121-.487.254-1,.4-1.581l.01-.037c.261-1,.483-1.923.675-2.8a31.536,31.536,0,0,0,.876-6.975c0-.108,0-.222,0-.327a13.946,13.946,0,0,0-.463-3.321,13.641,13.641,0,0,0-1.12-2.814c-.362-.692-.66-1.273-.982-1.905h0c-.377-.736-.812-1.586-1.273-2.474-2.324-4.478-5.633-10.535-9.149-14.816a32.155,32.155,0,0,0-4.968-4.951h0a26.8,26.8,0,0,1-6.155-6.9v0l-.913-1.374a63.956,63.956,0,0,0-6.625-8.89c-.51-.552-1.059-1.118-1.659-1.711-1.2-1.187-2.619-2.487-4.363-4-.229-.2-.451-.362-.675-.542a18.308,18.308,0,0,0-3.218-2.105c-.081-.042-.158-.071-.239-.111a17.734,17.734,0,0,0-3.134-1.155l-.017,0c-.679-.182-1.317-.315-1.906-.441-2.676-.564-4.52-.953-5.352-3.919a10.116,10.116,0,0,1-.234-5.153,7.8,7.8,0,0,1,.431-1.293,8.725,8.725,0,0,1,2.637-3.338,3.231,3.231,0,0,1,1.369-.6c.059-.012.128-.017.19-.027a13.813,13.813,0,0,1,1.45-.165c.308-.022.623-.049.933-.084.052-.007.1-.012.153-.017a2.726,2.726,0,0,0,2.482-1.554c.633-1.625-1.268-4.5-2.979-7.081a23.2,23.2,0,0,1-2.336-4.025c-.7-1.854-3.518-2.068-7.248-2.349-.108-.007-.229-.02-.337-.027-1.347-.1-2.789-.217-4.336-.431a18.989,18.989,0,0,1-5.96-1.664,8.456,8.456,0,0,1-1.384-.874,5.472,5.472,0,0,1-2.139-5.259c.015-.138.022-.273.044-.419l0-.025.01-.057c.667-4.013.906-5.451-.835-7.43a10.7,10.7,0,0,0-2.162-1.869,15.4,15.4,0,0,1-3.622-3.378c-.4-.49-.734-.941-1.039-1.369-.091-.128-.162-.239-.246-.362-.2-.291-.384-.569-.547-.83-.079-.128-.148-.244-.219-.364-.133-.229-.254-.443-.359-.643-.057-.106-.111-.207-.16-.305-.094-.19-.172-.355-.241-.512-.02-.047-.052-.108-.071-.153-.362.089-.724.165-1.081.231a25.923,25.923,0,0,1-4.6.409,1.183,1.183,0,0,0-.308.037h0a1.28,1.28,0,0,0-.766.684,4.7,4.7,0,0,0-.411,2.179,17.692,17.692,0,0,0,.739,4.565h0c.14.5.337,1.192.534,1.881a27.786,27.786,0,0,1,1.261,5.7c.091,2.216-.756,3.486-3.449,3.6a22.127,22.127,0,0,1-6.261-.62,21.227,21.227,0,0,1-5.751-2.361l-.325-.187c-.032.172-.079.364-.138.571l-.017.052C139.709,222.4,139.611,222.671,139.5,222.964Z"
                  transform="translate(-133.618 -192.641)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_278"
                  data-name="Path 278"
                  d="M173.064,319.986l.108-.172c.042-.069.091-.13.135-.2.069-.1.138-.207.212-.308.091-.131.194-.259.3-.384.074-.094.145-.187.227-.278.116-.135.251-.266.387-.4.079-.079.148-.158.234-.234a7.519,7.519,0,0,1,.726-.571l.044-.034a15.922,15.922,0,0,1,5.628-2.125h0v0a10.16,10.16,0,0,0,4.6-2.068,11.405,11.405,0,0,1,4.449-2.169c.1-.027.195-.057.3-.084.31-.076.6-.135.881-.177a7.584,7.584,0,0,1,.808-.084,7.921,7.921,0,0,1,3.407.615,3.3,3.3,0,0,0,.726.207,1.006,1.006,0,0,0,.96-.426,4.8,4.8,0,0,0,.588-.933,5.579,5.579,0,0,0,.416-3.572c-.059-.382-.135-.756-.212-1.123-.433-2.1-.82-3.988,1.438-5.724a7.267,7.267,0,0,1,3.346-1.472,18.753,18.753,0,0,1,1.989-.207c.347-.025.7-.052,1.069-.089a10.307,10.307,0,0,0,4.9-1.689c.633-.411,1.12-.714,1.568-.995,3.284-2.051,4.082-2.551,4.23-7.969.027-1.014.084-1.834.162-2.516a7.959,7.959,0,0,1,1.14-3.562c.057-.1.111-.192.17-.293a15.89,15.89,0,0,0,1.795-4.254h0c.308-1.068.561-1.854.768-2.5a8.063,8.063,0,0,0,.606-2.693c-.039-.926-.515-2.1-1.583-4.727l-.263-.647a27.365,27.365,0,0,0-1.886-3.806c-.111-.192-.214-.369-.31-.542l-.044-.076c-.079-.145-.155-.286-.224-.421l-.052-.1c-.076-.158-.148-.313-.209-.465l-.02-.059c-.052-.133-.1-.268-.135-.406l-.032-.131c-.032-.133-.059-.273-.079-.414,0-.032-.012-.064-.015-.1a5.06,5.06,0,0,1-.037-.566c0-.035,0-.071,0-.108,0-.172.007-.359.022-.554l.015-.158c.022-.239.052-.49.094-.768-.113.027-.224.049-.342.076l-.057.012a16.368,16.368,0,0,1-3.769.529,7.17,7.17,0,0,1-1.334-.133c-.487-.1-.918-.212-1.307-.315a6.875,6.875,0,0,0-1.795-.3,11.4,11.4,0,0,0-2.89.431,15.953,15.953,0,0,0-3.333,1.475c-1.869.99-3.833,2.029-5.842,2.048a5.606,5.606,0,0,1-3.048-.911,6.363,6.363,0,0,1-.613-.379c-.123-.094-.249-.155-.372-.244a10.615,10.615,0,0,0-6.155-1.967,32.978,32.978,0,0,0-6.729.721c-.867.16-1.775.345-2.73.544-1.763.369-3.193.65-4.456.847a24.6,24.6,0,0,1-3.255.32c-.3.005-.586.005-.876,0a32.907,32.907,0,0,1-8.331-1.716,7.585,7.585,0,0,0-3.1-.369c-2.487.33-3.336,2.634-4.5,5.781-.5,1.366-1.061,2.878-1.817,4.473a21,21,0,0,0-2.275,9.387,20.307,20.307,0,0,0,1.637,7.393c.251.618.507,1.236.815,1.871v0c.207.426.364.808.547,1.211a23.782,23.782,0,0,1,2.007,6.3c.044.305.108.62.133.916a.466.466,0,0,0,0,.059,12.778,12.778,0,0,1,.022,1.62c-.007.182-.039.359-.057.537-.034.359-.066.721-.13,1.073-.039.217-.1.436-.148.65-.071.31-.143.625-.236.936-.069.231-.153.465-.234.7-.066.2-.121.392-.2.591-.037.1-.222.608-.394,1.083q.355.536.793,1.115c3.964,5.224,4.052,2.883,7.386,4.683s1.891,5.4,7.386,8.646a34.492,34.492,0,0,0,6.1,3.068l.079-.123,0,0Z"
                  transform="translate(-113.349 -116.747)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_279"
                  data-name="Path 279"
                  d="M145.317,213.175c.042.084.094.175.143.268.094.172.207.364.327.564.064.106.126.212.2.322.148.232.32.485.5.748.071.106.138.2.217.315.278.384.588.8.95,1.243a14.067,14.067,0,0,0,3.329,3.107,11.983,11.983,0,0,1,2.42,2.093c2.194,2.494,1.925,4.109,1.172,8.619l-.012.081a4.209,4.209,0,0,0,1.061,3.8,5.11,5.11,0,0,0,.5.5,7.26,7.26,0,0,0,1.224.766,17.756,17.756,0,0,0,5.429,1.482c1.59.219,3.151.337,4.582.446,4.252.32,7.465.564,8.489,3.277a22.671,22.671,0,0,0,2.189,3.74c1.906,2.876,4.02,6.071,3.122,8.39-.886,2.282-2.937,2.435-4.806,2.573-.35.027-.682.057-.995.1-.1.012-.207.022-.3.039a2.072,2.072,0,0,0-.921.34,7.308,7.308,0,0,0-2.216,2.787,6.185,6.185,0,0,0-.357,1.081,7.1,7.1,0,0,0-.175,1.265,9.822,9.822,0,0,0,.4,3.141c.6,2.127,2.093,2.445,4.262,2.9a19.471,19.471,0,0,1,9.835,4.678c7.127,6.194,8.875,8.826,12.908,14.892l.911,1.374v0l0,0v0a25.405,25.405,0,0,0,5.52,6.3l.35.293h0v0c.379.313.766.635,1.169.987a32.991,32.991,0,0,1,3.993,4.158c4.308,5.249,8.292,13.048,10.589,17.549h0c.419.822.78,1.526.973,1.893a14.677,14.677,0,0,1,1.748,6.765,38.427,38.427,0,0,1-1.6,10.5c-1.6,6.111-1.361,6.529-.2,8.528a28.961,28.961,0,0,1,1.977,3.986l.263.65c1.13,2.784,1.632,4.02,1.684,5.2a8.83,8.83,0,0,1-.672,3.2c-.217.667-.48,1.482-.758,2.45h0a17.23,17.23,0,0,1-1.942,4.587c-.7,1.187-1.174,1.994-1.275,5.685-.167,6.192-1.093,6.77-4.9,9.151-.492.308-1.041.65-1.546.977a12.373,12.373,0,0,1-6.657,2.009,7.467,7.467,0,0,0-4.552,1.381c-1.549,1.189-1.248,2.659-.908,4.3.35,1.7.731,3.552-.332,5.633-.958,1.869-1.7,2.329-2.575,2.258a5.969,5.969,0,0,1-1.477-.406,7.878,7.878,0,0,0-1.716-.475,6.1,6.1,0,0,0-.867-.037c-.1,0-.175-.017-.276-.012a7.257,7.257,0,0,0-1.391.217,10.254,10.254,0,0,0-4.242,2.009,11.221,11.221,0,0,1-2.514,1.509c-.069.03-.145.057-.214.086-.322.128-.684.251-1.086.372-.258.079-.539.153-.837.226-.163.042-.315.084-.492.123v0a14.649,14.649,0,0,0-5.128,1.9l-.032.027a6.054,6.054,0,0,0-.613.485c-.071.066-.13.135-.2.2-.111.113-.224.224-.322.337-.076.089-.14.18-.212.271s-.158.2-.229.3-.148.219-.222.33c-.042.064-.086.128-.128.195.857.327,1.9.743,3.333,1.366,5.586,2.432,4.774,3.334,2.432,12.16s-.451,12.7,5.5,10.087,12.428-5.852,13.688-11.977,1.8-8.646,6.756-10.537,15.626-5.18,20.4-8.422,11.707-7.024,17.381-12.339,4.774-8.2,6.485-18.642-1.44-10.988-4.232-20.715-2.029-11.394-3.649-15.9-2.881-4.953-6.394-8.555-3.693-4.865-6.667-9.006-5.581-6.037-13.329-11.35-10.446-8.646-13.058-14.951-9.771-10.49-10.673-14.095,2.972-4.143,2.883-9.907,3.242-7.115,5.675-9.277a18.967,18.967,0,0,0,5.493-8.826c.99-3.513,5.943-3.693,10.087-3.782s5.042-5.855,9.727-8.287a20.863,20.863,0,0,0,2.029-1.2c-.126-.148-.268-.305-.352-.386-1.45-1.455-3.257-1.723-5.192-2.011-2.538-.377-5.283-.785-7.974-3.575-3.412-3.54-3.688-5.057-4.185-7.79a34.356,34.356,0,0,0-1.2-4.919c-1.485-4.579-4.414-5.623-8.541-7.093-1.549-.549-3.26-1.16-5.126-2.021-3.393-1.563-6.024-2.263-8.513-1.987s-4.9,1.534-7.849,3.88c-5.251,4.168-6.556,4.136-12.4,4-1.147-.027-2.487-.059-4.259-.059a87.256,87.256,0,0,0-11.566.544c-2.89.406-4.968,1.091-6.3,2.211a10.051,10.051,0,0,1-3.22,1.738l.059.128C145.16,212.863,145.231,213.013,145.317,213.175Z"
                  transform="translate(-116.921 -200.228)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_280"
                  data-name="Path 280"
                  d="M194.485,248.329c3.063,2.612,6.935-.81,9.548-3.693s5.313-2.07,7.2-5.224.451-5.852,2.972-8.284,6.305-2.883,4.685-6.576-1.891-3.782-5.584-2.972-8.287-.089-14.23,1.891c-4.865,2.162-3.242,1.711-5.675,5.224s-6.125,2.612-7.836,5.315-1.531,8.284,1.443,10.537S191.422,245.717,194.485,248.329Z"
                  transform="translate(-59.262 -169.618)"
                  fill="#ff3d4b"
                />
              </g>
              <g
                id="philipines"
                data-id="philippines"
                data-offset-top="-200"
                aria-selected={currentDialog?.id === "philippines"}
                transform="translate(835.608 2598.796)"
                onMouseEnter={onMouseOver}
                onClick={onClick}
                onMouseLeave={onMouseOut}
                fill="transparent"
              >
                <path
                  id="Path_281"
                  data-name="Path 281"
                  d="M281.593,268.311c1.3-.4,3.151-.135,2.656-2.477s-3.2-6.62-2.927-3.378c.044,2.117-1.62,2.162-1.62,3.6S280.288,268.718,281.593,268.311Z"
                  transform="translate(-175.263 -175.172)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_282"
                  data-name="Path 282"
                  d="M282.986,291.35c2.477-.359,5.133.542,4.818-2.25s-3.287-3.737-6.214-1.8c-2.115,1.307-4.323.63-3.287,2.748S280.509,291.71,282.986,291.35Z"
                  transform="translate(-177.693 -138.633)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_283"
                  data-name="Path 283"
                  d="M286.294,282.508c-2.253-3.063-9.185-.721-9.638,2.253.542,4.683.182,5.943-2.88,7.743s-5.675,3.063-4.5,5.766,2.612,2.521,5.4,4.683,1.982,6.305,4.774,4.774,1.078-1.982,1.169-7.566-.451-6.935,1.711-9.277S288.544,285.571,286.294,282.508Z"
                  transform="translate(-190.93 -146.25)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_284"
                  data-name="Path 284"
                  d="M289.229,291.351c.81-1.531,2.026-1.982.721-5.224s-2.3-5.628-4.459-2.972c-1.26,2.117-2.477,2.927-.854,5.224S288.419,292.882,289.229,291.351Z"
                  transform="translate(-169.188 -144.871)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_285"
                  data-name="Path 285"
                  d="M292.466,327.842c.182-1.3.766-1.755-.586-3.151s-3.917-2.341-3.331-.495c.945,2.792.586,2.341,2.117,4.459S292.286,329.149,292.466,327.842Z"
                  transform="translate(-162.455 -84.7)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_286"
                  data-name="Path 286"
                  d="M280.689,278.765c-3.063-1.8-5.042.271-7.2-1.8s-7.115-2.523-5.763,4.412c.539,4.594-1.8,4.953-.453,8.558s1.8,4.141,5.045.719,3.333-1.8,6.4-5.313S283.751,280.567,280.689,278.765Z"
                  transform="translate(-194.062 -154.216)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_287"
                  data-name="Path 287"
                  d="M265.951,310.268c1.036,1.755,1.531,3.693,2.839,2.026s2.117-.9,1.891-2.927-1.125-3.466-3.784-1.891C265.412,308.513,264.917,308.513,265.951,310.268Z"
                  transform="translate(-196.167 -108.623)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_288"
                  data-name="Path 288"
                  d="M266.817,265.181c-.719.9-.854,1.8.406,2.3s2.656,1.169,3.2-.857S268.213,263.423,266.817,265.181Z"
                  transform="translate(-194.893 -170.734)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_289"
                  data-name="Path 289"
                  d="M287.923,284.24c2.07,4.412,2.972,3.331,5.493,7.295s3.333,2.25,5.224.721-1.261-5.315-3.422-8.917-.81-4.594-1.172-9.1-2.162-3.873-7.475-3.693c-4.143-.539-4.683,2.073-2.162,6.665A64.212,64.212,0,0,1,287.923,284.24Z"
                  transform="translate(-170.605 -161.827)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_290"
                  data-name="Path 290"
                  d="M266.651,232.2c1.26.315,2.565.584,2.521-1.172s-1.891-2.568-2.836-.945C265.523,230.89,265.391,231.882,266.651,232.2Z"
                  transform="translate(-195.712 -222.077)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_291"
                  data-name="Path 291"
                  d="M238.259,309.116c2.43-1.891,1.979-4.144,4.5-6.485s6.034-3.6,8.735-5.4a19.664,19.664,0,0,0,7.026-7.566c1.529-3.242-.812-5.313-3.6-.99-3.422,4.412-4.953,5.042-7.115,6.753s-5.042,2.523-7.2,7.026S235.826,311.007,238.259,309.116Z"
                  transform="translate(-237.281 -138.722)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_292"
                  data-name="Path 292"
                  d="M263.7,229.449c1.169-.18,2.88.089,2.386-1.667s-2.115-1.891-3.242-.677C262.3,228.277,262.524,229.626,263.7,229.449Z"
                  transform="translate(-200.322 -226.308)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_293"
                  data-name="Path 293"
                  d="M269.425,263.469a17.822,17.822,0,0,0,7.474,4.954c3.242,2.253,3.693,1.8,6.216,5.766s2.341,3.964,6.665,6.125,7.383,3.151,5.133.18-7.295-5.675-7.925-9.459-1.711-3.87-5.136-6.753-4.862-6.125-8.555-4.323S266.995,260.948,269.425,263.469Z"
                  transform="translate(-191.668 -177.872)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_294"
                  data-name="Path 294"
                  d="M281.216,279.909c-.271,2.972,0,4.683,2.25,4.774s4.414.63,4.5-2.253S280.765,273.783,281.216,279.909Z"
                  transform="translate(-173.175 -151.912)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_295"
                  data-name="Path 295"
                  d="M260.419,287.7c2.341,5.675,3.422,7.836,5.222,5.315s1.893-8.107,3.6-4.954.18,4.774-.542,6.935-.539,5.584,1.8,7.566,2.883,3.873,6.576,2.792,4.323-1.172,4.141-5.315-4.141-9.094-5.133-12.967,3.242-6.935.271-10.9-4.414-6.3-1.891-5.946,2.7,3.6,5.493,2.794,1.982-.721,4.594-3.154,2.07-.268,5.224-3.422,4.594-5.855,3.781-8.826-3.781-2.612-2.972-7.474,3.6-6.756,1.531-9.639-1.531-1.169-6.305-3.151-4.414-4.052-9.636-5.045-10.449,1.531-11.709,9.727c-.99,5.584-1.081,6.576-.091,9.639s-1.44,4.771-1.62,8.376,2.521,5.675.271,8.735-5.133,4.774-5.224,1.711-5.493-6.123-5.4-3.331.091,3.513,2.972,7.834S258.077,282.031,260.419,287.7Z"
                  transform="translate(-215.175 -217.825)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_296"
                  data-name="Path 296"
                  d="M263.349,232.934c1.8.5,2.521.948,2.43-.675s-2.159-3.466-2.836-2.206C262.222,231.088,261.547,232.439,263.349,232.934Z"
                  transform="translate(-200.855 -221.443)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_297"
                  data-name="Path 297"
                  d="M253.374,287.214c2.61-2.341,5.764-2.881,4.954-4.592s-2.432-3.873-5.5-.63c-2.612,2.792-5.584,5.222-5.224,7.024S250.762,289.558,253.374,287.214Z"
                  transform="translate(-222.225 -147.353)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_298"
                  data-name="Path 298"
                  d="M321.6,290.251c.359-4.862-2.161-4.683-2.612-1.891s.359,5.315.81,8.2,1.531,3.513-1.172,4.594-6.214-1.982-8.2-.182-.448,2.432-.36,4.143-4.774.633-6.844,5.224c-1.352,2.253-4.143,6.664-6.576,5.675s-.719-4.052-3.782-6.034-6.214-2.883-7.836.721-2.7,5.042-6.844,5.133-6.665-.091-8.107,4.143-5.581,13.78-2.43,11.977,2.43-5.855,4.323-8.467,3.6-6.214,5.943-4.232-.45,7.2,2.612,6.756,2.972-1.352,5.313-3.154.271-6.573,5.766-3.782,8.826.9,8.1,5.493-4.052,2.253-2.253,8.917,2.433,10.449,6.756,12.248,5.584,4.774,8.555,1.8,5.315-2.07,5.224.539-3.151,6.756.091,5.855,4.052-2.433,4.953-5.855,2.162-3.422-.9-7.115-5.5-6.665-3.513-8.646,3.693,1.713,5.045-.9.359-6.576,2.521-4.774,6.485,2.341,4.505,8.014-2.432,11.438.18,8.917,0-5.493,3.24-9.545,7.117-5.766,4.956-9.818-5.766-2.521-7.745-4.414-1.711-1.258-.182-6.482,2.341-5.406,1.261-8.829-.719-3.781-4.5-7.024S321.235,295.116,321.6,290.251Z"
                  transform="translate(-194.574 -138.458)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_299"
                  data-name="Path 299"
                  d="M254.554,274.247c.18,2.612-2.048,5.47-.788,6.778s6.349-1.261,6.44-4.235S254.668,270.892,254.554,274.247Z"
                  transform="translate(-213.747 -158.41)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_300"
                  data-name="Path 300"
                  d="M296.243,324.671c.719,2.026,1.349,2.836,1.8,1.4s1.578-.766,1.531-4.1-2.117-4.008-2.026-2.115C297.368,322.374,295.521,322.644,296.243,324.671Z"
                  transform="translate(-151.324 -91.074)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_301"
                  data-name="Path 301"
                  d="M261.276,309.394c-.719.857-.9,1.219,1.128,2.524s3.873,2.162,3.961.044S262.763,306.785,261.276,309.394Z"
                  transform="translate(-202.93 -106.308)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_302"
                  data-name="Path 302"
                  d="M270.755,271.619c-1.44-2.432-6.438-5.177-8.376-5.9s-4.683-.99-4.818.992c-.315,1.529,0,1.529,1.62,3.511s2.253,2.3,2.477,5.451.812,5.584,2.927,7.474,4.279-1.443,5.36-3.737S272.2,274.052,270.755,271.619Z"
                  transform="translate(-207.792 -169.474)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <path
                  id="Path_303"
                  data-name="Path 303"
                  d="M258.6,313.062c-2.117.135-4.143,1.352-3.964,2.477s-.271.721,2.026,1.487,5.81.945,6.214-.9S261.08,312.431,258.6,313.062Z"
                  transform="translate(-211.919 -99.663)"
                  fill="inherit"
                  stroke="#ff3d4b"
                  strokeWidth="1"
                />
                <text
                  id="Philippines"
                  transform="translate(74.392 109.204)"
                  fill="#fff"
                  fontSize="30"
                  fontFamily="DINCondensed-Bold, DIN Condensed"
                  fontWeight="700"
                >
                  <tspan x="-51.435" y="0">
                    Philippines
                  </tspan>
                </text>
              </g>
              <g
                id="malaysia"
                data-id="malaysia"
                data-offset-top="-350"
                aria-selected={currentDialog?.id === "malaysia"}
                transform="translate(573.91 2788.884)"
                onMouseEnter={onMouseOver}
                onClick={onClick}
                onMouseLeave={onMouseOut}
                fill="transparent"
              >
                <path
                  id="Path_274"
                  data-name="Path 274"
                  d="M160.166,334.418c-.4.121-.8.273-1.2.423-.261.1-.519.182-.778.291-.436.187-.859.4-1.283.618-.209.106-.428.2-.635.313-.583.32-1.145.665-1.679,1.019-.032.022-.069.042-.1.062-.431.29-.827.588-1.209.886l-.231.18c-.359.293-.7.584-1,.872-.042.042-.076.081-.118.121a10.2,10.2,0,0,0-.7.766c-.042.052-.091.106-.13.157a4.854,4.854,0,0,0-.495.763,2.467,2.467,0,0,0-.111.254c-.03.091-.062.2-.1.327a1.456,1.456,0,0,0-.049.293h-.027c-.042.167-.084.337-.123.5,1.81,1.583,3.791,3.289,6.2,5.1,8.1,6.125,18.551,9.1,16.84,3.784s-9.1-8.737-11.259-16.754l-.1-.367c-.293.049-.586.113-.884.182C160.719,334.281,160.446,334.335,160.166,334.418Z"
                  transform="translate(-102.888 -258.909)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_275"
                  data-name="Path 275"
                  d="M190.88,387.034c.108.121.219.241.327.362.534.591,1.091,1.182,1.7,1.775a37.992,37.992,0,0,0,5.387,4.3c.313.209.606.416.938.628s.623.367.936.549a14.956,14.956,0,0,0,5.951,2.248,11.19,11.19,0,0,0,1.654.052,19.606,19.606,0,0,0,5.695-1.295v0a13.148,13.148,0,0,1,2.275-.611,9.61,9.61,0,0,1,1.672-.143,13.589,13.589,0,0,1,2.945.406,15.473,15.473,0,0,0,1.965.337c.234.022.475.034.726.037s.515,0,.79-.027a10.259,10.259,0,0,0,1.817-.315c.335-.086.687-.192,1.059-.32.121-.042.229-.089.347-.133a19.073,19.073,0,0,0,2.066-.879c.367-.184.719-.379,1.059-.576a31.933,31.933,0,0,0,4.368-3.213c.4-.337.805-.67,1.209-.992a18.832,18.832,0,0,1,3.944-2.553h0c3.085-1.418,5.544-1.029,8.9-.5.446.071.909.145,1.391.217s.987.14,1.514.207c1.054.128,2.2.234,3.474.281.884.032,1.795.049,2.716.039a35.556,35.556,0,0,0,5.554-.455,22.824,22.824,0,0,0,2.666-.613,14.244,14.244,0,0,0,4.562-2.228,10.756,10.756,0,0,0,1.359-1.258,10.151,10.151,0,0,0,1.827-2.72c.121-.263.227-.5.325-.714.2-.428.359-.78.507-1.1l.214-.463c1.056-2.292,1.834-3.974,2.46-5.729.177-.5.345-1.007.5-1.536s.305-1.078.446-1.664c.071-.3.14-.6.207-.911.271-1.258.522-2.7.778-4.441.071-.5.18-.965.271-1.448a30.663,30.663,0,0,1,2.984-8.826,19.209,19.209,0,0,1,2.7-3.868,11.145,11.145,0,0,1,3.075-2.425,43.945,43.945,0,0,1,10.867-3.272,30.39,30.39,0,0,1,3.526-.411c.369-.02.729-.027,1.073-.027s.682.015,1,.039.623.059.911.106a5.512,5.512,0,0,1,1.5.448,8.442,8.442,0,0,1,1.46.891c.2.148.372.3.539.443s.32.291.473.433.276.259.419.382c.029.025.061.047.089.071.118.1.236.2.367.29.069.049.155.094.229.143.1.066.2.133.318.194.094.052.212.1.315.143.121.057.231.113.369.165s.281.086.421.13.286.1.458.14c.155.039.352.071.529.108.195.039.372.084.593.118.195.032.438.056.657.084.249.032.477.066.761.094.241.025.537.039.8.059.3.022.576.047.908.062a3.4,3.4,0,0,1,.175-.793c.99-2.974,4.052-2.523,8.1-2.974s5.224.091,6.485-1.529-.271-3.425-4.323-3.513-3.151-1.622-2.43-3.873,4.862-2.07,10-.81,5.133-2.253,6.034-4.414-2.521-1.982-7.115-2.521-3.961-.542-6.214-3.693c-2.883-3.828-4.82-3.425-6.44-3.693s-3.693.451-5.315-1.352.677-2.972,1.711-5.269-1.216-2.253-4.547-4.143-.766-2.612-3.287-4.368-5.877-1.147-8.489,4.075-3.964,1.8-6.576,5.045-.719,4.594-3.063,7.2-3.6-.089-6.933,1.711.18,3.424-1.172,8.2c-.47,1.662-.918,2.548-1.492,2.994l.039.148h0s.6,2.189,1.093,4.171v.007l.01.044.005.02v0l0,.007a3.686,3.686,0,0,1,.086.911,5.31,5.31,0,0,1-.446,1.928,6.478,6.478,0,0,1-.5.928,5.851,5.851,0,0,1-3.412,2.536l0,0-.007,0-.1.022H278.6a4.383,4.383,0,0,1-1.238.1,5.574,5.574,0,0,1-1.536-.355,14.609,14.609,0,0,1-1.615-.711c-.278-.14-.529-.263-.758-.364a2.768,2.768,0,0,0-.559-.2.457.457,0,0,0-.249.007,1.848,1.848,0,0,0-.453.308c-.153.135-.308.3-.478.47a7.942,7.942,0,0,1-2.026,1.628,7.682,7.682,0,0,1-.908.4,9.25,9.25,0,0,1-1.253.384,7.217,7.217,0,0,1-1.344.185,4.693,4.693,0,0,1-1.691-.261,4.394,4.394,0,0,1-1.411-.738,5.435,5.435,0,0,1-.7-.7c-.071-.086-.13-.185-.2-.276-.138-.185-.276-.372-.4-.576-.074-.121-.138-.256-.207-.382-.106-.2-.212-.391-.305-.6-.067-.148-.131-.305-.192-.461-.057-.135-.118-.263-.17-.4-2.009,2.533-2.3,4.857-6.128,8.1-4.144,3.513-5.222,4.683-7.2,8.287s-2.344,5.537-6.214,7.16-5.81-.721-9.909.091-2.521,1.755-6.123,2.972-4.909,1.891-6.935,4.818-1.462,5.111-2,8.129-2.521,3.24-5.269,4.412-3.782-.451-6.935-1.531-6.889,1.307-8.467-1.531-4.006-2.792-5.539-1.711a6.2,6.2,0,0,0-1.795,2.974c.3.386.625.817.835,1.086.076.093.15.187.226.281Q190.136,386.189,190.88,387.034Z"
                  transform="translate(-47.068 -303.518)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_276"
                  data-name="Path 276"
                  d="M160.247,312.377c-.027.044-.061.1-.091.143s-.074.116-.116.18c-.143.224-.3.458-.475.7l-.02.027c-.167.234-.352.47-.547.707l-.16.19c-.214.249-.433.492-.672.721l-.015.015a7.517,7.517,0,0,1-.721.6c-.064.049-.13.094-.2.14a4.445,4.445,0,0,1-.812.458v0c-.207.086-.453.17-.687.256-.4.145-.827.278-1.29.4a12.174,12.174,0,0,1-3.624.443,10.2,10.2,0,0,1-1.081-.111,9.342,9.342,0,0,1-2.093-.591,9.079,9.079,0,0,1-1.679-.879,22.683,22.683,0,0,1-5.232-5.249c-.34-.433-.67-.854-1-1.258s-.675-.785-1.032-1.142h0l-.032-.03a3.045,3.045,0,0,0-.812-.551,6.161,6.161,0,0,0-1.994-.5,9.851,9.851,0,0,0-1.376-.059,11.5,11.5,0,0,0-1.283.118c-.126.017-.234.042-.35.064-.266.049-.515.1-.736.167-.116.034-.219.069-.32.1a4.931,4.931,0,0,0-.812.443c.192.231.389.465.6.707,6.034,7.024,4.008,11.032,3.378,17.428s2.792,11.168,2.883,15.67,3.242,5.133,7.474,10.449,1.62,4.771,4.862,10.717,7.026,6.573,14.951,9.816c5.04,2.061,8.22,4.779,11.953,8.058l.012-.042c.02-.069.052-.143.074-.212a3.544,3.544,0,0,1,.167-.446c.039-.086.091-.175.138-.261.076-.143.155-.286.246-.431.062-.094.128-.19.2-.286.1-.14.207-.283.323-.428.079-.1.162-.2.249-.3.123-.143.256-.288.394-.433.1-.1.187-.2.288-.3.15-.148.313-.3.475-.443.1-.094.2-.187.31-.278.185-.16.382-.318.579-.473.1-.081.194-.16.3-.239.3-.226.611-.453.936-.672l.017-.012.012-.007c.591-.4,1.224-.78,1.881-1.137.217-.121.443-.219.665-.33.455-.231.908-.458,1.376-.66.273-.116.547-.207.822-.313.433-.163.862-.323,1.295-.455.3-.086.583-.15.874-.219s.574-.167.857-.219c-1.7-5.628-2.112-2.834-5.163-9.016-3.333-6.756-2.612-12.251-2.524-15.131s2.026-8.016-.089-14.5-7.14-11.372-11.551-15.6c-3.287-3.154-6.374-4.407-9.449-6.48-.052.1-.2.389-.453.8C160.308,312.268,160.279,312.325,160.247,312.377Z"
                  transform="translate(-130.984 -298.446)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_304"
                  data-name="Path 304"
                  d="M218.858,324.17c.076.167.158.327.239.483.057.106.113.212.172.31a5.2,5.2,0,0,0,.305.453c.054.074.1.153.16.221a3.84,3.84,0,0,0,.534.542,3.142,3.142,0,0,0,2.221.67,5.419,5.419,0,0,0,.995-.14,6.252,6.252,0,0,0,3.481-2.016,5.871,5.871,0,0,1,.977-.835,2.593,2.593,0,0,1,.421-.263c.862-.369,1.736.074,2.775.6a7.282,7.282,0,0,0,2.521.9,2.93,2.93,0,0,0,.9-.052h0l.047-.012.052-.012a2.01,2.01,0,0,0,.192-.071A4.452,4.452,0,0,0,237.2,323.1a3.707,3.707,0,0,0,.662-2.674l-.012-.047v0h0l-.022-.086c-.32-1.273-.8-3.055-1.1-4.178-.958,0-2.231-.3-4.062-.337-4.412-.089-5.493,2.883-8.014,4.5-2.329,1.5-4.119.384-6.364,2.4.066.192.133.392.192.559.069.192.14.382.214.564C218.75,323.929,218.8,324.049,218.858,324.17Z"
                  transform="translate(-3.344 -285.6)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <text
                  id="Malaysia-2"
                  data-name="Malaysia"
                  transform="translate(122.09 32.116)"
                  fill="#fff"
                  fontSize="31"
                  fontFamily="DINCondensed-Bold, DIN Condensed"
                  fontWeight="700"
                >
                  <tspan x="-42.315" y="0">
                    Malaysia
                  </tspan>
                </text>
              </g>
              <path
                id="Path_571"
                data-name="Path 571"
                d="M281.811,408.892a4.06,4.06,0,0,0,2.511-.054,1.927,1.927,0,0,0,1.2-1.285,7.534,7.534,0,0,0-.234-3.033c-2.7.645-5.126,1.037-6.785,2.457.167.15.342.3.519.438A7.876,7.876,0,0,0,281.811,408.892Z"
                transform="translate(658.598 2633.025)"
                fill="#ff3e4c"
              />
              <path
                id="Path_570"
                data-name="Path 570"
                d="M275.706,382.814c.9-2.3-.675-7.7-3.828-5.269-1.62,1.349-2.162,1.3-1.081,4.907s-.99,5.63,2.073,4.641S274.8,385.111,275.706,382.814Z"
                transform="translate(646.504 2592.69)"
                fill="none"
                stroke="#ff3e4c"
                strokeWidth="1"
              />
              <g
                id="Group_734"
                data-name="Group 734"
                transform="translate(1197.55 2926.536)"
                opacity="0.101"
              >
                <path
                  id="Path_354"
                  data-name="Path 354"
                  d="M457.693,368.516c-1.081-1.216-2.477-.362-.428,1.462,2.162,2.792,2.162,3.422,2.432,7.474s.089,9.008,3.06,5.855,3.6-2.341,1.622-7.295S458.773,369.732,457.693,368.516Z"
                  transform="translate(-279.151 -346.976)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_355"
                  data-name="Path 355"
                  d="M454.09,366.061c.9-.721,1.711-1.531,1.081-2.794s-2.07-2.341-1.891-.81C453.324,364.889,453.191,366.78,454.09,366.061Z"
                  transform="translate(-283.463 -356.105)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_356"
                  data-name="Path 356"
                  d="M453.722,367.192c.854.9,1.44,2.026,2.521,1.485s.99-.99.044-1.935S452.686,365.346,453.722,367.192Z"
                  transform="translate(-283.14 -349.942)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_357"
                  data-name="Path 357"
                  d="M466.577,419.634c-1.283-1.957-2.679-2.363-3.6-3.287s-3.469-1.462-2,.135c1.733,1.711.2,1.465,2.454,3.107s5.448,4.3,5.628,2.861S467.862,421.594,466.577,419.634Z"
                  transform="translate(-272.847 -277.511)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_358"
                  data-name="Path 358"
                  d="M449.641,364.31c2.927,1.4,1.576.633,5.224,3.289s5.9,3.466,4.594,1.17-6.2-3.907-7.689-5.077-3.176-1.96-3.794-1.137C446.984,363.859,446.713,362.914,449.641,364.31Z"
                  transform="translate(-292.198 -355.358)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_359"
                  data-name="Path 359"
                  d="M458.2,367.2c1.125-.226,2.07-.226,1.846-1.622s-3.107-2.115-2.927-.584C457.205,366.654,457.072,367.42,458.2,367.2Z"
                  transform="translate(-277.844 -352.557)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_360"
                  data-name="Path 360"
                  d="M467.538,416.01c-.923.655-1.4,1.036-.586,1.487s1.443.495,1.736-.158S468.663,415.313,467.538,416.01Z"
                  transform="translate(-264.059 -276.994)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_361"
                  data-name="Path 361"
                  d="M457.646,402.662c.876,1.216,1.824,2.386,2.883,1.938s1.866-.677.81-1.915S456.811,401.131,457.646,402.662Z"
                  transform="translate(-277.245 -297.734)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_362"
                  data-name="Path 362"
                  d="M452.567,406.488c-1.979-1.62-3.016-.4-4.862-1.349s-4.279-2.794-5.044-1.667-.089,2.253,3.469,4.188c3.24,1.982,3.557,1.8,5.133,4.143s2.521,4.818,3.781,3.828.318-.948-.9-3.875S454.549,408.11,452.567,406.488Z"
                  transform="translate(-299.379 -295.56)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_363"
                  data-name="Path 363"
                  d="M536.991,483.56c-3.242-1.531-3.693-.992-5.357-3.425s-2.568-2.565-4.909-3.422-4.459.63-5.854-.719.586-2.659,1.125-4.865-2.026-1.98-4.953-2.792-4.774.135-6.034-2.341,1.374-4.254-.652-6.056-4.865.9-8.646-.227-3.422-4.276-4.683-7.024-.362-4.232-1.4-6.709-6.711-3.2-9.1-4.55-1.8-2.612-3.2-4.412-4.075-3.762-6.145-6.463.539-5.764,6.573-4.954,5.313-.992,5.943-4.683-1.44-9.1-7.834-8.737-8.826-1.261-11.35-4.144-4.592-3.06-7.745-3.151-4.591-2.432-3.782-5.4-.9-5.854-4.953-9.1-4.774-.359-8.467-4.052-2.927-4.008-6.709-6.529-6.034,0-10.448-2.162-3.331-3.333-8.285-3.063-6.216-1.44-11.8-3.6-8.284-2.523-12.878-6.4a19.27,19.27,0,0,0-6.684-2.849v53.03l-.074,2.74-.342.212c-.01.007-3.688,2.213-4.008,5.1a6.622,6.622,0,0,0,.194,2.681,5.242,5.242,0,0,0,.389.968c.054.1.13.2.192.3a11.471,11.471,0,0,0,2.652,2.794l.271.219v32.658c2.233,1.389,4.833,1.216,7.319.495,3.107-.9,6.756-1.62,10.988-.18s5.18.721,9.639-.495,6.28-3.942,6.28-6.283-1.576-4.774-.583-6.394,2.521-1.172,5.4-2.432,2.386-5.042,5.177-6.169,4.729.542,6.756-.766a5.889,5.889,0,0,1,6.709-.268c2.3,1.484,3.964,3.466,8.6,3.558s11.776,3.353,13.08,7.767,3.018,3.646,4.1,6.753,2.927,4.19,5.36,5.136,4.862,1.664,6.529,3.873,2.25,3.781,4.276,7.43,5.045,2.071,8.6,3.422,3.063,1.3,7.341,1.4,4.412-.63,8.285-.495,6.17.721,8.961,3.873,6.214,4.818,10.222,3.693,4.459.315,5.946-.047S540.233,485.091,536.991,483.56Z"
                  transform="translate(-384.295 -356.06)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_364"
                  data-name="Path 364"
                  d="M423.534,380.985c1.105-.022,1.758-.136,1.645-1.216s-2.117-2.25-2.341-1.012C422.657,379.926,422.431,381.007,423.534,380.985Z"
                  transform="translate(-328.167 -331.953)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_365"
                  data-name="Path 365"
                  d="M430.98,383.621a4.132,4.132,0,0,0-1.327-2.836c-.677-.7-1.736-1.642-2.184-.158-.158.564.022.788,1.125,2.117S431,384.859,430.98,383.621Z"
                  transform="translate(-321.247 -329.591)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_366"
                  data-name="Path 366"
                  d="M488.9,372.394c-4.144-1.216-9.006-1.307-7.972,2.115s1.847,7.925-1.078,8.646-3.154-.674-5.271,2.162-2.972,7.295-8.24,6.394c-3.378-.495-3.964.226-7.386.992s-2.115-1.711-6.844-.362-5.448.812-9.277.047-11.079-1.172-11.842.539,1.708,2.206,4.276,4.1.406,5.313,4.638,4.727,4.414-3.646,7.115-1.935,2.839,4.323,7.566,3.961,5.448.091,7.521-1.935,2.026-1.172,6.438-2.432,6.891-3.466,8.016-5.4,3.557-2.295,7.25-3.557,1.036-2.88,2.388-5.448,2.521-1.352,3.511-4.367S493.044,373.608,488.9,372.394Z"
                  transform="translate(-316.235 -341.6)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_367"
                  data-name="Path 367"
                  d="M458.345,411.715c-.428.473-.721,1.014.113,1.509s1.283.675,1.664-.2S458.953,410.974,458.345,411.715Z"
                  transform="translate(-276.649 -283.336)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_368"
                  data-name="Path 368"
                  d="M443.46,365.095c2.836.539,3.1-.721,4.727-.044s2.432.721,1.891-.766-1.125-1.622-2.657-3.018-3.646-3.016-4.638-.586C442.2,363.472,440.621,364.556,443.46,365.095Z"
                  transform="translate(-300.2 -359.43)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_369"
                  data-name="Path 369"
                  d="M511.3,404.542c-3.063-2.748-2.432-2.927-5.089-4.547s-4.865-2.792-6.216-4.323-1.755-.406-3.782-1.667-6.079-1.3-2.206,2.026c2.792,2.792,3.331,1.622,6.258,3.784s4.5,2.43,6.214,4.412,6.17,4.368,7.342,3.513S514.359,407.29,511.3,404.542Z"
                  transform="translate(-226.265 -310.04)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_370"
                  data-name="Path 370"
                  d="M495.889,402.063c-1.664-1.352-3.107-1.487-5.539-4.008-3.063-3.107-4.276-.18-3.917,1.307s2.432,3.06,4.683,4.141,4.143.992,5.9,2.117,1.529,1.44,2.521.766S497.555,403.414,495.889,402.063Z"
                  transform="translate(-235.063 -304.971)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_371"
                  data-name="Path 371"
                  d="M497.158,402.756c-.18,1.172-.428,1.6.473,1.982s2.839.519,2.927-.539S497.609,401.272,497.158,402.756Z"
                  transform="translate(-219.547 -296.895)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_372"
                  data-name="Path 372"
                  d="M500.109,399.067c-.81-1.4-2.206-.945-4.907-2.565s-3.873-2.477-4.954-4.279-2.523-1.982-5.224-3.378-5.81-.495-1.937,2.253c2.524,2.206,3.6,4.953,6.125,6.618s2.7.948,6.349,1.938S500.921,400.463,500.109,399.067Z"
                  transform="translate(-242.526 -317.376)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_373"
                  data-name="Path 373"
                  d="M481.852,394.834c-.453,1.261-.992,2.341.763,2.839s3.2.224,2.477-1.216S482.977,392.808,481.852,394.834Z"
                  transform="translate(-242.325 -308.865)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_374"
                  data-name="Path 374"
                  d="M480.9,391.631c-2.521-1.755-2.477-2.7-3.69-4.73s-2.568-1.034-4.235-3.422-1.755-5.493-2.927-4.683-1.216,1.982-.135,4.459,3.828,5.178,3.378,7.206c.18,2.386.721,2.477,2.477,4.188s2.3,1.216,2.792,3.961,1.758,4.953,3.919,5.763,3.961.406,4.5-2.071,2.073-3.961-.315-6.529S483.422,393.389,480.9,391.631Z"
                  transform="translate(-260.264 -331.308)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_375"
                  data-name="Path 375"
                  d="M520.55,413.438c-2.881-2.3-7.521-4.235-8.6-2.253-1.125,1.216.857,2.568,2.432,3.693a14.949,14.949,0,0,0,7.566,2.7C524.829,417.626,523.433,415.732,520.55,413.438Z"
                  transform="translate(-198.154 -284.969)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_376"
                  data-name="Path 376"
                  d="M511.361,407.826c-3.333-3.107-10.493-6.709-10.067-.967-.088,4.143-.539,5.044,4.055,4.953s6.844,3.425,9.006,1.081S514.692,410.935,511.361,407.826Z"
                  transform="translate(-213.325 -294.534)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_377"
                  data-name="Path 377"
                  d="M510.517,403.124c-1.036-2.745-3.469-5.313-3.289-2.43.047,3.287-.451,3.2,1.667,6.079s3.693,3.151,5.4,5.5,2.974,3.781,3.557,2.477,0-1.982-1.8-4.55S511.551,405.872,510.517,403.124Z"
                  transform="translate(-204.601 -300.955)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_380"
                  data-name="Path 380"
                  d="M547.354,418.569c-1.531,0-2.206.857-1.622,1.622a2.227,2.227,0,0,0,3.107.18C549.787,419.47,549.109,417.894,547.354,418.569Z"
                  transform="translate(-148.585 -273.21)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_381"
                  data-name="Path 381"
                  d="M544.564,416.573c-.9,1.216-1.307,2.073.044,2.073s3.289.584,3.469-.812S545.509,415.356,544.564,416.573Z"
                  transform="translate(-151.199 -276.662)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_382"
                  data-name="Path 382"
                  d="M546.257,413.034c-.542-1.261-2.344-5.042-3.334-1.935-.675,1.62-1.081,2.612.542,3.016S546.8,414.294,546.257,413.034Z"
                  transform="translate(-153.251 -285.642)"
                  fill="#ff3d4b"
                />
                <path
                  id="Path_383"
                  data-name="Path 383"
                  d="M540.708,413.344c-.945.81-1.711,1.531-.18,1.982s3.964.448,4.323-1.037S542.961,411.9,540.708,413.344Z"
                  transform="translate(-157.161 -281.674)"
                  fill="#ff3d4b"
                />
              </g>
              <path
                id="Path_569"
                data-name="Path 569"
                d="M184.849,327.33a4.283,4.283,0,0,0,.044-4.232c-.81-1.846-2.612-2.7-4.075-1.014-1.261,2.612.63,3.964,1.169,6.755S184.172,328.952,184.849,327.33Z"
                transform="translate(515.16 2511.181)"
                fill="#ff3e4c"
              />
              <text
                id="Thailand-2"
                data-name="Thailand"
                transform="translate(605 2661)"
                pointerEvents="none"
                fill="#fff"
                fontSize="31"
                fontFamily="DINCondensed-Bold, DIN Condensed"
                fontWeight="700"
              >
                <tspan x="-40.471" y="0">
                  Thailand
                </tspan>
              </text>
              <path
                id="myanmar"
                data-id="myanmar"
                data-offset-top="-50"
                aria-selected={currentDialog?.id === "myanmar"}
                onMouseEnter={onMouseOver}
                onClick={onClick}
                onMouseLeave={onMouseOut}
                fill="transparent"
                d="M130.15,370.073c5.222-2.341,8.014-8.375,13.509-10.357s5.852-4.143,9.277-8.735,5.584,2.161,6.394,6.3,4.144,6.485,6.573,14.5.721,12.248,1.849,17.879,2.972,8.735,7.113,16.48,3.784,5.855,3.6,10.808,1.44,12.068,1.982,16.754-.766,8.149-3.56,12.743c-2.211,3.641-.236,9.321-.49,14.794.153-.074.308-.15.463-.231s.313-.16.465-.249c.234-.13.463-.278.692-.426.14-.094.286-.177.423-.276.256-.177.5-.377.743-.576.1-.086.214-.163.318-.254a10.449,10.449,0,0,0,.975-.977,9.479,9.479,0,0,0,2.314-5.727,35.208,35.208,0,0,1,1.955-9.065c.419-1.327.874-2.762,1.31-4.4a25.963,25.963,0,0,1,2.125-5.177c1.31-2.607,2.583-5.138,2.371-9.535a24.521,24.521,0,0,0-.583-4.249c-.288-1.288-.645-2.474-.992-3.629-.886-2.949-1.716-5.709-1.3-9.523a19.552,19.552,0,0,0-.785-8,20.884,20.884,0,0,0-4-7.218,24.98,24.98,0,0,0-1.8-1.842c-2.885-2.76-5.333-5.1-.561-11.221a34.983,34.983,0,0,0,5.532-9.006,15.105,15.105,0,0,0,.544-8.6c-.748-3.705-3.245-5.414-5.564-7-1.7-1.167-3.321-2.277-4.271-4.057a8.124,8.124,0,0,0-1.96-2.324c-.1-.089-.2-.18-.3-.266-.246-.212-.488-.424-.719-.638-1.617-1.5-2.708-3.25-1.32-8.462,1.216-4.555-.98-7.4-2.91-9.9a16.75,16.75,0,0,1-2.218-3.383,5.51,5.51,0,0,1-.48-2.208h0l0-.847c0-.825-.007-1.605.022-2.359q.015-.377.044-.746c.227-2.957,1.179-5.6,5.027-9.183,1.285-1.2,2.4-2.186,3.444-3.033.1-.086.207-.163.31-.244a27.074,27.074,0,0,1,2.9-2.038,28.794,28.794,0,0,1,5.584-2.5c.6-.209,1.224-.419,1.913-.633,1.056-.332,1.987-.655,2.866-.975,1.167-.423,2.208-.847,3.134-1.283.426-.2.844-.406,1.236-.615a20.881,20.881,0,0,0,2.888-1.9c.682-.527,1.347-1.093,2.026-1.733a7.3,7.3,0,0,1,.935-.734c.081-.054.16-.106.241-.158a8.383,8.383,0,0,1,1-.542h0a10.124,10.124,0,0,1,1.056-.391c.091-.027.177-.054.268-.079.345-.1.689-.185,1.029-.246l.012,0c.35-.064.689-.108,1.027-.143.086-.01.172-.017.259-.025.313-.027.618-.047.906-.054l.039,0c.3-.007.574,0,.837,0l.214.007c.224.01.436.022.62.037l.052,0h0c0-.049,0-.089-.007-.135a8.885,8.885,0,0,1-.062-1.354c.007-.165.034-.313.052-.473a7.424,7.424,0,0,1,.121-.842c.037-.17.089-.325.138-.485a6.61,6.61,0,0,1,.236-.687c.064-.153.135-.3.209-.451.1-.2.212-.4.327-.6.086-.138.17-.276.261-.406.128-.19.263-.372.4-.549.094-.118.185-.239.281-.352.16-.19.325-.372.492-.551.086-.094.167-.187.254-.278q.351-.366.7-.716l.034-.037a10.216,10.216,0,0,0,1.95-2.356c.153-.308.286-.576.4-.82l.062-.131c.091-.192.17-.367.239-.519l.047-.106c.071-.162.13-.308.18-.428l.007-.02a4.214,4.214,0,0,0,.187-.544l.007-.034a.542.542,0,0,0,.017-.086l0-.03V279.4a27.833,27.833,0,0,0-4.742-1.664c-1.019-.29-2.142-.611-3.54-1.078a14.349,14.349,0,0,1-6.212-4.134,20.606,20.606,0,0,1-4.062-7.31,13.392,13.392,0,0,0-2.755-4.806,11.17,11.17,0,0,1-2.954-7.145,52.686,52.686,0,0,0-1.529-9.082,14.909,14.909,0,0,0-3.668-6.6c-2.226-2.226-6.027-1.635-9.181-1.145a13.55,13.55,0,0,1-4.614.256v0l-.687-.155c-2.858-.645-3.486-.785-4.139-5.168a18.729,18.729,0,0,1,.246-6.908,17.732,17.732,0,0,1,2.76-6.216c2.484-3.56,11.451-11.692,13.181-13.248-.234-1.514-1.086-7.086-1.494-9.89-.195-1.332-.436-3.466-.66-5.623-.33-3.176-.625-6.408-.677-7.223,0-.007-.692-.153-1.566-.355-2.038-.47-4.769-1.1-5.426-3.74-.222-.881-.382-1.718-.532-2.511-.436-2.282-.8-4.185-2.644-6.283v0a15.081,15.081,0,0,0-5.168-3.484q-.558-.247-1.135-.47l-.084.1-.234.3c-.1.121-.207.271-.32.419-.081.111-.158.209-.246.332q-.31.425-.662.945c-.062.091-.128.2-.192.3-.182.276-.369.566-.559.876-.094.153-.187.315-.281.475-.17.286-.34.586-.507.894-.1.18-.194.362-.291.549-.172.332-.34.68-.5,1.034-.081.175-.167.35-.246.532-.236.537-.463,1.091-.665,1.664l0,.007c-.145.409-.325.945-.5,1.455-1.406,4.136-1.837,5.4-6.775,8.041a24.993,24.993,0,0,1-7.8,2.757c-1.75.389-3.974.884-7.378,1.891-7.25,2.144-8.065,4-9.038,8.476-.064.3-.128.608-.2.931l-.054.258c-.064.3-.123.588-.18.852l-.074.337c-.057.263-.1.5-.153.753a31.363,31.363,0,0,0-.608,6.084,16.027,16.027,0,0,1-.884,5.771c-.394,1.113-.655,2.075-.9,3.013l-.054.2a31.548,31.548,0,0,1-4.609,10.353q-.38.606-.689,1.165a17.459,17.459,0,0,0-2.063,5.65h0c-.335,1.672-.529,2.592-2.135,3.22-.155.062-.342.116-.527.172-.108.032-.2.069-.325.1h0c-.089.025-.172.035-.261.054a12.026,12.026,0,0,1-1.231.222,17.348,17.348,0,0,1-1.9.13c-.364.005-.724.005-1.083.005-.638,0-1.275,0-1.913.044a9,9,0,0,0-4.318,1.268c-2.457,1.484-2.666,3.3-3.124,7.282-.145,1.243-.313,2.693-.579,4.414-.02.125-.042.261-.062.391-.084.515-.17,1.041-.271,1.608-.47,2.615-.783,4.673-1.044,6.384-.214,1.408-.4,2.578-.611,3.71a42.613,42.613,0,0,1-1.979,6.952c-.079.222-.128.4-.2.608a30.009,30.009,0,0,0-1.283,5.229,17.738,17.738,0,0,1-2.716,7.435,30.332,30.332,0,0,1-3.924,5.264,8.643,8.643,0,0,0-2.511,4.308h0l-.7-.16A31.845,31.845,0,0,0,86.783,296c3.06,3.154,8.735,7.206,12.068,11.35s1.261,6.844,6.485,10.988,9.232,11.391,10.042,15.444,3.6,12.16.721,18.1-5.766,11.8-2.073,15.222S124.926,372.414,130.15,370.073Z"
                transform="translate(372.263 2283.462)"
                stroke="#ff3e4c"
                strokeWidth="1"
              />
              <path
                id="Path_568"
                data-name="Path 568"
                d="M84.387,275.19c-.4,1.711-.268,5.178.766,5.584s1.8-.675,1.758-3.2S85.064,273.3,84.387,275.19Z"
                transform="translate(374.496 2442.788)"
                fill="#ff3e4c"
              />
              <g
                id="indonesia"
                data-id="indonesia"
                data-offset-top="-250"
                aria-selected={currentDialog?.id === "indonesia"}
                transform="translate(456.979 2689.327)"
                onMouseEnter={onMouseOver}
                onClick={onClick}
                onMouseLeave={onMouseOut}
                fill="transparent"
              >
                <path
                  id="Path_306"
                  data-name="Path 306"
                  d="M306.755,401.651c-4.774-1.531-7.745-4.143-11.079-2.162s-6.753,3.6-10.357,8.826c-.212.214-.426.414-.64.608a37.711,37.711,0,0,0,2.58,6.724c1.915-2.226,5.946-3.309,9.23-4.54,3.6-1.352,4.143-4.5,9.1-4.953S311.529,403.182,306.755,401.651Z"
                  transform="translate(210.642 -64.701)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_307"
                  data-name="Path 307"
                  d="M322.664,363.736c1.893-.224,2.659.406,2.388-1.62s-.945-3.919-3.828-4.232-6.125-.315-3.693,2.386C319.153,362.207,320.773,363.963,322.664,363.736Z"
                  transform="translate(257.42 -124.686)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_308"
                  data-name="Path 308"
                  d="M290.513,400.678c1.982-2.073-.539-4.235-4.774-3.063-4.323.451-6.034,1.62-3.063,2.972S288.531,402.749,290.513,400.678Z"
                  transform="translate(205.539 -66.88)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_309"
                  data-name="Path 309"
                  d="M310.134,336.147c1.531-2.386,2.477-6.482-.99-5.178-2.026,1.125-3.784,1.667-2.927,4.1S308.605,338.535,310.134,336.147Z"
                  transform="translate(241.805 -164.195)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_310"
                  data-name="Path 310"
                  d="M355.556,354.949c1.667,2.386,3.513,4.727,5.044,3.378s.406-1.8-1.3-3.828-3.242-2.3-4.594-3.378-4.188-1.036-2.7.406C352.944,353.238,353.892,352.561,355.556,354.949Z"
                  transform="translate(308.448 -135.467)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_311"
                  data-name="Path 311"
                  d="M353.748,356.782c-2.659-1.982-1.036,1.664.4,2.7s4.774,1.846,7.657,1.622,3.917-.586,4.279-1.8-.677-2.341-4.5-1.576S357.034,358.9,353.748,356.782Z"
                  transform="translate(309.538 -126.942)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_312"
                  data-name="Path 312"
                  d="M278.249,400.829c1.4.135,3.107.586,2.612-.9s-2.341-3.242-2.836-1.664C276.989,399.657,276.853,400.693,278.249,400.829Z"
                  transform="translate(199.744 -66.356)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_313"
                  data-name="Path 313"
                  d="M300.262,362.082c2.521.945,4.862-.63,1.261-2.341s-5.675-1.172-7.61-2.341a7.874,7.874,0,0,0-6.891-.047c-2.07,1.172-5.539,1.893-6.258,3.107s.359,1.263,2.88,2.883,5.946-.495,9.594-2.162S297.741,361.137,300.262,362.082Z"
                  transform="translate(204.597 -126.386)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_314"
                  data-name="Path 314"
                  d="M274.292,354.523c2.972,2.162,4.052,2.159,7.745,1.979s3.151-2.071,5.943-1.8,10.5,1.664,12.61.224,6.98-1.531,11.529.36,4.052,2.612,9.41,2.341,9.144-.583,12.79-5.628,7.295-6.573,8.331-11.8-.4-5.5-3.466-2.073a83.441,83.441,0,0,1-7.364,7.679c-3.558,3.287-4.188,3.287-10.4,3.331s-4.279-.99-9.232-.089-5.808,1.125-8.691-1.081c-4.8-3.58-12.723-.561-14.658-2.5s-6.349-3.693-9.144-2.115-2.925,4.276-5.672,5.719-3.378.224-6.8,1.979-4.345,3.85-5.517,6.6-.945,9.321-.406,12.428-.7,3.942-3.489,6.825-3.425,4.368-3.649,8.1,1.261,4.458-.089,7.61.4,4.685-1.758,6.935-4.525,3.942-4.616,7.814.135,4.006,4.055,7.881,3.917,1.576,6.349,5.9,2.162,5.313,1.034,9.23-1.44,6.485-3.917,10.584-.99,6.394,2.7,6.935,3.287-1.081,6.889-.182,5.855-.315,6.349-2.745a22.564,22.564,0,0,0-1.036-12.475c-2.026-5.133.655-4.572.113-7.093s.091-3.151,1.261-5.808.945-4.37-1.216-8.737-1.935-6.17,2.073-6.709,4.074.561,6.551,2.5,3.242,2.521,1.487,7.474-.315,2.883,2.521,6.349,3.2,2.073,5.675,3.513,2.341,2.117.226,5.36.268,5.357,3.737,7.024,2.971-1.3,4.188-4.862,2.565-1.711,8.691-2.026,5.7-1.15,5.968-3-1.172-1.711-4.594-2.521-3.063-1.4-4.323-3.693a8.437,8.437,0,0,1-1.012-6.957c.763-2.568-.091-3.334-1.893-6.261s-3.422-2.927-4.367-5.045-3.287-4.232-4.638-5.313a4.916,4.916,0,0,1-.7-6.236c1.531-2.253,7.117-2.883,10.089-4.953s3.693-3.6,6.214-6.485,6.394-2.883,6.844-4.774-3.331-1.352-5.852.542-2.974.268-6.125-.542-3.333,2.792-4.953,3.513-6.844-3.333-8.738-1.622-3.511,7.925-8.555,8.016-4.232-1.891-6.664-6.485-4.323-3.781-6.125-9.006-1.62-6.125.271-9.456S271.321,352.359,274.292,354.523Z"
                  transform="translate(156.558 -156.676)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_315"
                  data-name="Path 315"
                  d="M302.065,354.957c0,3.331,1.081.271,2.253,5.4s-.542,2.523,1.44,7.386.451,1.8,4.143,6.756,4.232,10,5.943,7.475,1.172-3.334-1.711-6.935-4.774-2.25-5.855-6.665-2.88-8.826,0-8.2,3.334.539,6.125,2.341,5.5,2.344,5.854-.089-.091-4.683-3.333-5.045-4.683.091-4.953-2.521-.542-2.7,2.521-3.422,2.615.089,4.505-3.154,1.62-7.925-1.711-6.573-4.776.271-5.224,3.961-.9,5.5-3.784,5.946-4.323.63-2.972-1.982,3.693-1.172,3.422-4.232-4.412-4.232-2.7-6.756,3.309-2.272,2.994-4.21-1.081-1.576-2.521-.315a12.968,12.968,0,0,0-3.986,7.046c-.63,3.334-2.25,3.334-.451,7.027S302.065,351.623,302.065,354.957Z"
                  transform="translate(234.856 -160.734)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_316"
                  data-name="Path 316"
                  d="M275.333,413.92c-2.612.542-5.5,1.622-3.331,2.883s4.141,1.62,5.313-.362S277.315,412.029,275.333,413.92Z"
                  transform="translate(190.998 -43.557)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_318"
                  data-name="Path 318"
                  d="M270.362,399.958c-1.935-1.216-3.6-.583-3.331.9-.135,1.216,1.891,2.568,3.331,2.117s2.523.135,3.74-1.531S272.3,401.174,270.362,399.958Z"
                  transform="translate(184.799 -63.91)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_319"
                  data-name="Path 319"
                  d="M276.339,399.479c-1.3-1.352-3.693-2.883-3.826-1.352-.362,1.4.495,2.117,2.656,3.2s4.323,1.667,3.737.495S277.646,400.828,276.339,399.479Z"
                  transform="translate(192.738 -66.581)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_320"
                  data-name="Path 320"
                  d="M266.539,412.731c-2.162,1.081-3.242,3.964-1.169,4.052s7.115-.271,6.573-2.971S269.242,411.02,266.539,412.731Z"
                  transform="translate(180.789 -45.97)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_321"
                  data-name="Path 321"
                  d="M290.358,405.959a9.233,9.233,0,0,1,.246,3.759,3.342,3.342,0,0,1-2.061,2.248,5.446,5.446,0,0,1-3.41.108,9.279,9.279,0,0,1-3.309-1.738c-.2-.16-.4-.325-.588-.5l-.047.067c-2.974,4.5-5.675,8.2-5.136,11.529s2.523,2.25,5.584-.362,5.224-1.078,8.828-3.242a30.359,30.359,0,0,0,8.592-7.47,37.369,37.369,0,0,1-2.88-7.068A17.672,17.672,0,0,1,290.358,405.959Z"
                  transform="translate(197.932 -58.096)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_322"
                  data-name="Path 322"
                  d="M281.658,355.256a5.122,5.122,0,0,0-5.18-.99c-3.378,1.484-4.907,2.836-3.557,3.737s.992.044,3.242-.542,4.1,2.206,5.539.945S282.871,356.293,281.658,355.256Z"
                  transform="translate(192.737 -130.21)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_323"
                  data-name="Path 323"
                  d="M247.815,400.462c-2.972-1.44-6.8-2.073-9.277-.542-2.162,1.307-2.386,1.758-2.656,3.469s-1.938,2.7-.315,3.826,3.063,1.172,5.719-.4,5.4-.315,8.691-1.667S250.787,401.9,247.815,400.462Z"
                  transform="translate(137.818 -64.322)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_324"
                  data-name="Path 324"
                  d="M315.726,389.3c-2.162-5.672.453-4.683,1.622-8.735s3.107-7.25,6.349-8.87,7.386.9,10.537.81,2.523-3.964.63-7.206-4.143-3.961-9.1-5.133-3.693-3.693-2.07-7.386,1.44-3.06-1.352-6.753-2.612-3.063-3.513-7.928,3.018-4.1,3.469-8.87a4.9,4.9,0,0,0-1.549-4.557l-1.327-.1c-.222-.015-.4-.037-.6-.054-.268-.022-.537-.044-.78-.071-.315-.034-.6-.074-.881-.116-.212-.03-.426-.059-.615-.094-.259-.047-.49-.1-.716-.15-.162-.039-.335-.074-.485-.116-.207-.057-.391-.121-.574-.185-.125-.042-.261-.084-.377-.13-.17-.069-.318-.14-.465-.214-.1-.049-.2-.094-.29-.143-.143-.081-.268-.165-.394-.249-.071-.049-.153-.1-.219-.145-.131-.1-.249-.194-.369-.3-.049-.042-.106-.084-.155-.128-.143-.126-.281-.254-.421-.384l-.059-.054a8.358,8.358,0,0,0-1.778-1.334c-.116-.064-.217-.128-.345-.19a3.8,3.8,0,0,0-.726-.246,9.213,9.213,0,0,0-2.24-.214c-.618.007-1.29.049-1.992.123s-1.438.177-2.189.305c-.5.084-1,.18-1.509.283a44.845,44.845,0,0,0-8.043,2.427c-.33.143-.633.283-.9.421a8.331,8.331,0,0,0-.9.591,10.5,10.5,0,0,0-1.817,1.581,15.8,15.8,0,0,0-1.27,1.605,20.925,20.925,0,0,0-1.172,1.92,26.494,26.494,0,0,0-1.876,4.466,33.484,33.484,0,0,0-1.221,5.333c-.14.967-.283,1.839-.423,2.642-.19,1.063-.382,2.006-.588,2.866-.145.6-.3,1.169-.458,1.711s-.33,1.064-.515,1.578c-.091.258-.185.514-.283.771-.2.512-.409,1.027-.638,1.561s-.475,1.083-.741,1.669-.551,1.206-.862,1.878c-.362.788-.771,1.672-1.044,2.27a11.94,11.94,0,0,1-3.572,4.493,15.616,15.616,0,0,1-5.072,2.5,31.608,31.608,0,0,1-8.582,1.128c-.955.01-1.9-.007-2.816-.042a45.435,45.435,0,0,1-5.128-.5c-.495-.074-.968-.148-1.426-.222-1.177-.185-2.233-.352-3.242-.421a9.408,9.408,0,0,0-4.828.808h0a16.833,16.833,0,0,0-3.42,2.194c-.133.1-.266.2-.4.308-.34.276-.68.556-1.024.842-.825.687-1.669,1.389-2.6,2.066q-.465.34-.963.67a20.553,20.553,0,0,1-4.725,2.327,12.206,12.206,0,0,1-4.121.749l-.037,0a14.24,14.24,0,0,1-2.972-.414,12.268,12.268,0,0,0-2.617-.364,9.946,9.946,0,0,0-3.484.657h0a20.769,20.769,0,0,1-6.162,1.381,12.438,12.438,0,0,1-1.822-.059c-.3-.032-.6-.111-.9-.167a18.609,18.609,0,0,1-6.6-2.844,43.449,43.449,0,0,1-3.666-2.583,37.51,37.51,0,0,1-2.9-2.531c-.044-.042-.081-.084-.126-.128-.367-.357-.721-.716-1.056-1.071-.143-.15-.273-.3-.411-.451-.227-.249-.458-.5-.675-.743-.155-.18-.3-.357-.455-.534-.187-.219-.374-.438-.554-.657-.162-.195-.323-.391-.48-.588-.1-.126-.207-.251-.308-.374-.379,1.29-.753,2.78-1.174,4.4-1.172,4.5-3.6,2.792-4.1,9.816s1.105,7.5,2.23,10.065.4,5.089,1.44,9.592.495,4.729,4.865,7.566,6.8,1.216,7.115,5.045-1.78,4.885.741,8.577,1.938,4.414,1.216,8.737.5,4.276,3.693,5.268,3.806-1.1,7.319.832,3.466.677,8.555-.4,5.5.133,5.584,4.547-.18,5.493,3.063,4.594,4.235-2.883,6.844-2.524,3.469.948,6.261-1.44,4.5-3.828,7.519-2.071,3.267.293,4.8,3.624,1.891,1.172,5.672-.766,6.935,1.576,8.016,4.774-.271,6.844,3.6,8.287,3.109-2.748,4.82-4.459,3.6-.359,7.654-.992,1.622-2.925,5-3.646,2.477,3.693,2.659,5.584,2.836.63,4.006-2.026,1.172-5.36-.359-8.961-.879-2.41-.066-6.236,1.664-2.883,2.88-5.268,1.128-3.737.135-6.576-4.254-3.624-2.363-7.137,4.323-5.584,6.305-7.657,7.745-.63,10.537-3.511S317.888,394.97,315.726,389.3Z"
                  transform="translate(66.355 -179.786)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_325"
                  data-name="Path 325"
                  d="M307.657,368.8c-3.107-2.341-3.063-3.6-6.98-2.3s-4.953.315-7.026.766-.268,3.737,1.352,5.4c2.117,2.927,2.3,3.242,6.125,2.612s2.792-.135,5.628-1.847S310.764,371.146,307.657,368.8Z"
                  transform="translate(222.397 -112.642)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_326"
                  data-name="Path 326"
                  d="M283.668,406.641c.633-1.352-.674-1.349-2.071-2.745a49.626,49.626,0,0,1-4.638-4.37c-2.25-2.565-5.855-1.62-11.032-.36A82.327,82.327,0,0,0,255.57,402.9c-2.792,1.4-3.693,0-3.919,1.667s1.622,1.935,6.037,2.883,5.537.539,11.391-.315,5.81.224,8.917,1.261S283.038,407.992,283.668,406.641Z"
                  transform="translate(162.33 -65.976)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_327"
                  data-name="Path 327"
                  d="M249.676,398.335c-4.547-.9-8.24-1.758-7.79.583.044,1.711.4,1.758,2.386,3.018s.945,2.341,4.5,4.323,6.034,2.883,7.61.857,2.568-2.524.766-4.953S254.223,399.233,249.676,398.335Z"
                  transform="translate(148.026 -66.698)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_328"
                  data-name="Path 328"
                  d="M268.956,410.662c-3.964-2.073-1.44-3.063-5.854-4.323a19.455,19.455,0,0,0-10.537-.451c-3.063.719-5.313,1.62-2.972,3.782s6.123-.451,9.1,2.073c4.5,3.06,1.531,4.774,6.394,5.675s7.024,1.169,8.2-1.352S272.919,412.732,268.956,410.662Z"
                  transform="translate(157.928 -55.068)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_329"
                  data-name="Path 329"
                  d="M306.27,354.026c-.315-1.846-1.216-2.612-2.7-3.6s-4.008-3.2-3.964.406c-.091,2.341.857,2.881,3.063,3.6S306.585,355.873,306.27,354.026Z"
                  transform="translate(232.455 -137.698)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_330"
                  data-name="Path 330"
                  d="M83.5,281.569c.18,2.208.226,4.37,1.172,3.2s1.891-.675,1.664-2.477S83.277,279.858,83.5,281.569Z"
                  transform="translate(-83.489 -237.518)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_331"
                  data-name="Path 331"
                  d="M477.051,446.923a6.679,6.679,0,0,1-.7-2.061,8.643,8.643,0,0,1-.042-2.42c.33-2.979,3.316-5.244,4.34-5.938V381.349h.069v-.125c-2.536-.463-4.527-.569-5.36-1.381-1.622-1.576-2.927-1.44-7.657-1.576s-4.774.406-9.816-1.3-8.218-6.012-14.838-6.507-4.008-.586-5.539-2.659-4.008-2.071-6.891-1.667-1.935,1.083-4.953,2.073-3.737,1.711-5.133,5.089-2.026,2.432-4.459,3.016-5.965.744-7.5,3-1.575,2.927-3.2,5.042-5.042,2.839-5.988,6.305-5.451,5.719-9.277,4.052-5.36-2.925-5.99-4.232a12.265,12.265,0,0,0-6.576-6.485c-4.323-1.755-3.287-4.1-3.378-5.9s.766-3.917.362-6.349,1.374-4.572,1.733-6.734-1.261-3.737-2.565-5.719-.633-2.836-1.307-4.368-5.045-1.938-8.1-1.531-5.315-.315-6.576-2.117-3.513-.9-5.4-2.3-3.961-.763-6.034.857-4.909,5-7.386,4.909-4.232-.99-5.4.224-2.836,5.584-5.269,3.828-3.6-5.042-6.529-2.568,0,3.784,1.037,5.586a7.348,7.348,0,0,0,6.258,3.917c3.063-.047,8.4-.069,10.83.832s4.008,3.693,4.638,7.115,1.352,2.792,5.719,4.55,4.865.135,9.053-1.216,6.756-1.037,8.287.766.99,4.456-2.164,3.828-3.375-1.263-6.665.359-3.87,4.008-5.943,2.836-4.594-1.531-8.06.091-4.008,1.349-2.253,3.511,4.729,1.4,8.467,2.524,2.5.113,5.111,3.535-.133,3.242-.224,6.844,2.927,3.378,5.36,2.612,2.836-2.748,3.287-5.045,5.448-1.667,6.935.359,4.909,4.774,7.519,5.36,7,4.931,12.723,6.507,9.589-1.667,13.915-.224,2.927,2.656,6.258,5.539,4.909.586,11.889,2.88,4.616,3.671,7.77,6.554,4.636,1.755,6.258,4.367.9,2.386,2.836,6.305,3.649,3.06,3.154,6.485.832,5.108,2.319,6.911,2.159,3.107.315,5.36-3.242-.18-6.125.721-4.818,3.06-7.115,7.113-2.477,2.432-4.052,5.36,2.386,4.143,5.719,2.839,6.8,0,9.636-.135,3.422-2.208,5.943-3.422,3.425.224,6.667-.182,4.232-2.612,9.006.091,1.891,3.2,5.313,6.079,6.012,4.345,8.536,7.137l.155.167V450.245A11.491,11.491,0,0,1,477.051,446.923Z"
                  transform="translate(262.888 -138.636)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_332"
                  data-name="Path 332"
                  d="M344.126,388.155a20.2,20.2,0,0,0-.677,7.834c.135,3.2-2.836,4.323-.315,5.315s3.873,1.62,5.133-1.758,2.883-5,3.2-8.691.451-4.459-.539-8.1S345.386,382.256,344.126,388.155Z"
                  transform="translate(294.611 -90.404)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_333"
                  data-name="Path 333"
                  d="M89.075,266.356c.315-1.307-.675-5.315-1.485-1.982-.857,3.334-.9,4.909-.406,6.261s-.451,2.295-1.036,4.5-1.3,1.349-.99,4.907,1.576,6.217,2.162,4.774,0-2.3.315-5a9.792,9.792,0,0,1,1.352-4.683c.81-1.531-.091-3.107-.315-4.55A11.014,11.014,0,0,1,89.075,266.356Z"
                  transform="translate(-81.16 -263.08)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_335"
                  data-name="Path 335"
                  d="M94.317,304.131c-1.172-2.117-4.188-2.927-3.513-.812.766,2.253.81,3.875,1.891,5.63s3.378.81,3.018-.677A11.065,11.065,0,0,0,94.317,304.131Z"
                  transform="translate(-72.937 -206.063)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_336"
                  data-name="Path 336"
                  d="M337.5,383.288c-1.938.315-.677-1.169-3.018-2.474-3.557-2.3-.945,5.178.9,6.438s2.792.542,3.873-.854,2.117-3.6,1.531-5.18S339.434,382.975,337.5,383.288Z"
                  transform="translate(280.863 -91.597)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_337"
                  data-name="Path 337"
                  d="M339.5,371.981c3.375,1.4,4.84,3.762,7.452,6.1s4.052.63,2.792-4.414-2.612-5.042-5.5-7.566-3.782-1.8-8.016-1.44c-4.119.923-7.226,1.689-9.387.879a11.573,11.573,0,0,0-5.089-1.036c-1.125.18-4.774,2.208-7.071,2.3s-3.287-.81-6.305,0-5,1.487-5.808,3.287,1.935.81,4.953-.224,3.063-1.216,5.4.495,3.243,2.253,4.727.675,2.568-1.935,6.62.362,4.188,2.026,7.295.36S336.126,370.585,339.5,371.981Z"
                  transform="translate(236.597 -115.122)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_338"
                  data-name="Path 338"
                  d="M310.136,359.434c-.9-1.982-3.422-2.792-5.537-4.323s-5.451-1.169-4.1,1.44c.945,2.253-.586,4.505,3.6,4.594S311.037,361.416,310.136,359.434Z"
                  transform="translate(233.314 -129.862)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_339"
                  data-name="Path 339"
                  d="M329.37,392.908c-2.927,2.974-4.818.812-5.584,3.38s.583,2.477,1.3,5.537,1.352,3.107-1.125,5.224-2.521,3.558-.406,2.792,2.206-1.216,3.378-3.107,2.386-2.657,3.917-4.232.857-3.11,2.568-4.909.451-2.117,1.711-4.279S332.884,388.5,329.37,392.908Z"
                  transform="translate(265.258 -76.765)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_340"
                  data-name="Path 340"
                  d="M314.244,396.426c-.315,1.443.586,1.307,1.62,3.828s2.206,3.917,2.927,2.388.495-3.154-1.081-5.089S314.963,393.23,314.244,396.426Z"
                  transform="translate(253.771 -70.42)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_341"
                  data-name="Path 341"
                  d="M321.854,354.46c3.425.766,3.425-2.432,6.891-4.1s3.018-1.485,1.755-2.745-4.5-1.711-8.376-.63C318.117,348.7,318.432,353.694,321.854,354.46Z"
                  transform="translate(261.115 -141.305)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_342"
                  data-name="Path 342"
                  d="M165.921,410.009c-1.586,1.44-1.874,3.026-.647,4.537s3.6-.288,3.457-1.945S167.72,407.919,165.921,410.009Z"
                  transform="translate(34.965 -49.316)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_343"
                  data-name="Path 343"
                  d="M262.955,485.933c-.766-4.323-.677-3.873.63-8.826s3.242-4.594,1.711-10.493-1.105-7.544-1.194-11.236,2.7-3.287,2.341-5.9,1.4-2.026-1.982-5.313-6.17-7.342-8.826-7.206-3.309,2.1-4.884-1.642-.812-4.1-3.558-6.216-3.873-2.656-4.505-7.159-4.052-5.584-7.43-5.719-3.986-.653-6.327-4.434.9-7.026,2.117-8.917,2.7-4.368-.359-6.8-5.741-1.283-7.994-2.319-.4-3.2-.315-6.125.451-5.178-3.828-5.313-4.547,4.1-7.566.81-.9-4.638-4.953-5.943-3.646-1.081-5.808-4.323-2.656-3.063-5.675-3.964-3.4,2.455-10.468-1.824-5.4-3.466-8.287-8.42-.227-4.638-6.261-7.61-5.855-2.972-8.959-3.784a12.227,12.227,0,0,1-7.679-7.046c-1.26-3.334-5.584-4.055-7.881-7.027a18.389,18.389,0,0,1-3.378-8.959c-.271-2.344-.608-2.59-6.236-2.186s-6.529,3.018-9.592,1.172-3.6-2.477-6.847-2.162-7.25-1.8-7.295-4.279-4.235-5.808-8.24-5.224-5.539,3.513-1.622,9.008,4.953,10.493,7.026,11.347,3.693-.18,5.357,3.333-.675,3.557,5.224,5.628,8.467,2.388,10.449,5,8.262,5.653,9.614,10.562-1.261,7.25,3.063,9.771,6.665.586,10,3.558,3.74,2.521,4.55,5.672,2.836,5.766,3.873,7.883-3.806,6.778,2.679,12.359,6.125,3.154,9.727,7.386,4.459,4.459,5.675,7.566,1.846,9.547,5.586,13.24c3.69,6.214,4.412,11.978,6.664,14.681s4.141,3.6,6.573,6.3,6.935,3.784,9.548,8.287-4.414,3.964,4.412,8.917,9.818,1.44,15.222,7.745S237.961,482.1,241.115,485.3s3.737-1.26,5.448-.675,1.3,1.125,3.378,2.295,2.162.677,3.466-.719,2.792-.857,5.224.359S263.721,490.256,262.955,485.933Z"
                  transform="translate(-60.038 -192.325)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_344"
                  data-name="Path 344"
                  d="M122.213,357.667c1.081,2.568,2.612,6.62,5.045,5.946s3.151-.047,1.485-3.875S120.231,351.633,122.213,357.667Z"
                  transform="translate(-27.398 -129.198)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_345"
                  data-name="Path 345"
                  d="M109.462,330.919c-2.117-1.056-7.093,1.014-4.055,1.4,2.051.633,3.085.451,4.528,1.96s2.474,2.432,2.77,1.014S111.577,331.978,109.462,330.919Z"
                  transform="translate(-52.845 -164.337)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_346"
                  data-name="Path 346"
                  d="M297.071,409.419c-3.154-1.352-5.584.539-9.008,1.979s-8.735,1.982-10.266-.9,1.169-6.125-4.323-5.946-6.214,2.341-9.186-1.352-2.253-7.024-6.756-5.943-7.024,1.8-9.1-1.172-3.333-8.464-6.305-3.06-.539,8.2-6.123,7.295-5.675-2.792-11.889-2.432-6.216,2.792-9.1.81-2.883-2.25-5.675-5.042-5.584-4.414-9.456-4.235-5.133.992-7.386-1.711-5.763-2.88-9.186-2.972-6.889-.945-10.4,1.216-7.745,4.5-7.657,7.836,4.323,4.052,8.287,4.683,2.521,1.62,5.133,4.774,6.844.451,11.259,2.792c4.323,1.44,6.034,2.792,10.178,4.232s10.985-.542,13.058-1.8,7.206-.99,11.8,1.534,8.558-.091,13.24,2.971,1.891,4.232,9.186,4.592,6.847-.9,9.907-.18,4.909-.044,8.782,2.208,7.657.719,10.448-.542,3.511-.091,6.485,1.172,1.62-2.344,5.4.719,8.376,3.693,9.006,1.443-1.531-3.693-.18-6.216S300.223,410.768,297.071,409.419Z"
                  transform="translate(36.056 -85.572)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_347"
                  data-name="Path 347"
                  d="M118.925,340.178c-3.242-3.018-9.008-1.172-4.055,2.521a22.392,22.392,0,0,1,5.269,5.855c1.622,2.386,3.063,1.891,2.974-.495S122.168,343.194,118.925,340.178Z"
                  transform="translate(-40.433 -152.575)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_348"
                  data-name="Path 348"
                  d="M226.849,390.17c-1.214,1.487-2.071.992,1.216,2.386s5.045,3.333,5.315,1.578S228.427,388.55,226.849,390.17Z"
                  transform="translate(124.514 -77.852)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_349"
                  data-name="Path 349"
                  d="M232.021,400.079c-1.034,2.253-.451,2.026-1.755,3.964s-1.667,2.026.315,3.69a4.983,4.983,0,0,0,6.485.091c1.62-1.531.135-2.206,1.261-4.594,1.26-2.477,1.44-2.972.63-4.458S233.058,397.829,232.021,400.079Z"
                  transform="translate(129.52 -65.922)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_350"
                  data-name="Path 350"
                  d="M217,394.488c3.378-.315,5,.81,8.6-.81s2.656-2.026,1.62-3.154-3.693-.99-6.709.362c-2.3.81-4.235.224-7.117.495s-3.24.044-3.646,1.8S213.624,394.8,217,394.488Z"
                  transform="translate(101.061 -77.869)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_351"
                  data-name="Path 351"
                  d="M231.281,397.917c-1.443-1.4-4.235-.271-7.836.719-1.96.97-.564,2.546.473,3.806s4.166,2.905,6.687,3.986,1.578-1.216,2.883-4.1S232.721,399.313,231.281,397.917Z"
                  transform="translate(119.679 -66.911)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_352"
                  data-name="Path 352"
                  d="M176.621,360.6c-1.263-4.818-5.855-4.592-8.152-3.287s-5.224,2.7-5.763,4.774,2.925,3.513,4.5,3.333,5.357,1.979,6.844,3.242,4.323,5.943,6.079,9.321,4.143,3.2,6.665,3.242,1.8-2.7-1.664-6.261-3.693-2.836-6.576-6.98S178.017,365.019,176.621,360.6Z"
                  transform="translate(32.24 -126.486)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_353"
                  data-name="Path 353"
                  d="M179.865,363.756c-2.25,1.755-3.466,2.432-2.026,5.539s3.917,4.774,6.844,3.331S183.829,360.378,179.865,363.756Z"
                  transform="translate(53.536 -116.848)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_378"
                  data-name="Path 378"
                  d="M336.17,415.927c-.992-.182-1.714.539-3.557.451-4.1.268-4.188,3.2-2.073,4.276s4.008-1.216,5.224-2.071S337.16,416.106,336.17,415.927Z"
                  transform="translate(275.692 -39.67)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_379"
                  data-name="Path 379"
                  d="M334.284,416.608c-1.307.45-3.11,1.352-5.584,2.253-1.487.224-.135-.227-3.289-.9s-4.909.224-6.17,2.162-3.016,4.771-.99,4.5,3.422-1.172,5.4-.63,3.016,1.711,5.268,2.206,4.774-1.664,6.034-3.151,3.649-1.938,2.568-3.693S335.589,416.157,334.284,416.608Z"
                  transform="translate(258.373 -38.753)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <path
                  id="Path_385"
                  data-name="Path 385"
                  d="M109.54,419.146c-1.583,2.6-1.44,4.181.721,6.054s3.6,2.159,3.026-1.155S112.135,414.68,109.54,419.146Z"
                  transform="translate(-46.957 -37.237)"
                  fill="inherit"
                  stroke="#ff3e4c"
                  strokeWidth="1"
                />
                <text
                  id="Indonesia-2"
                  data-name="Indonesia"
                  transform="translate(447.021 259.673)"
                  fill="#fff"
                  fontSize="30"
                  fontFamily="DINCondensed-Bold, DIN Condensed"
                  fontWeight="700"
                >
                  <tspan x="-45.285" y="0">
                    Indonesia
                  </tspan>
                </text>
              </g>
              <text
                id="Myanmar-2"
                data-name="Myanmar"
                transform="translate(520 2557)"
                pointerEvents="none"
                fill="#fff"
                fontSize="31"
                fontFamily="DINCondensed-Regular, DIN Condensed"
              >
                <tspan x="-47.662" y="0">
                  Myanmar
                </tspan>
              </text>
            </g>
          </MapSvg>
        </MapContainer>
      </Box>
    </CenterBlock>
  )
}

export default Map
